/**
 * cart-redesign.js — B2C cart redesign helpers
 *
 * <qmtx-cart-coupon> — inline coupon field for the redesigned cart/drawer.
 *   Applies the code via Shopify's native /discount/CODE endpoint (the legacy
 *   name="discount" cart field does NOT apply reliably at checkout). Once it
 *   sticks, order/product discounts appear in cart.cart_level_discount_applications
 *   with their amount. Drawer refreshes in place; cart page reloads. Feedback
 *   (applied → blinks / invalid) shows only after pressing "Aplicar".
 *
 * <qmtx-shipping-estimator> — inline "Envío" card (Shopify shipping_rates.json).
 */
(function () {
  'use strict';

  // B2C only. B2B pages never load this file (it ships with the B2C cart
  // sections), but if one ever did, none of these writers may touch a B2B cart.
  if (window.__qmtxB2B) return;

  var FLASH_KEY = 'qmtx-coupon-flash';

  // The cart carries _qmtx_b2b_cart='1' once it is a B2B cart (written by
  // assets/b2b-cart-hygiene.js). A retail tab left open across a B2B login
  // shares that cart: its attribute and upsell writers must then do nothing
  // (they put "Tramos de regalo" / _pb_origen on #13776). Updated on every
  // cart read that reaches the progress bar. Only while a B2B session is live
  // elsewhere: the first B2C page RENDER of a marked cart empties and unmarks
  // it (layout/theme.liquid, B2C branch), so a retail session never keeps it.
  var qmtxB2BCart = false;
  function qmtxNoteCart(cart) {
    if (cart && cart.attributes) qmtxB2BCart = cart.attributes._qmtx_b2b_cart === '1';
  }

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // BOGOS: marcador oficial de exclusión.
  //
  // BOGOS engancha el carrito por DOS vías (leído en su `gift.min.js`):
  //   1. `overrideFetch`  → envuelve window.fetch
  //   2. `listenAPI`      → un PerformanceObserver sobre entradas `resource`
  //                         de tipo xmlhttprequest/fetch
  // y en AMBAS descarta cualquier URL que contenga la cadena `_bogos-ignore`:
  //   overrideFetch: `l = ["_bogos-ignore"].some(p => c.includes(p))` → no
  //                  llama a checkEventCart
  //   listenAPI:     `r = ["sca_attr_cart","_bogos-ignore","bogos_discount"]
  //                  .some(...)` → `if (_ && !r && Y.cart.test(c))` → salta
  //
  // Sin este marcador, CADA lectura nuestra del carrito le dispara a BOGOS una
  // ronda completa `checkEventCart → checkAllOffers → auto add all gifts →
  // POST /cart/update.js`, y como su `refreshCart` repinta el drawer, nuestro
  // MutationObserver vuelve a leer el carrito: realimentación.
  //
  // Se pone SOLO en las peticiones de LECTURA y en el atributo de pedido. Las
  // mutaciones de verdad (añadir producto del upsell, aplicar cupón) deben
  // seguir siendo visibles: cambian lo que el cliente tiene derecho a recibir.
  function qmtxQuiet(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + '_bogos-ignore=1';
  }

  // GET JSON por XMLHttpRequest: fuera del alcance del fetch-override de BOGOS,
  // que en plena digestión de regalos puede tragarse promesas de fetch (patrón
  // del watchdog del upsell). cb(null) en cualquier fallo.
  function qmtxXhrJson(url, cb) {
    try {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) {}
        cb(data);
      };
      xhr.onerror = function () { cb(null); };
      xhr.send();
    } catch (e) { cb(null); }
  }

  // Base de cálculo de los umbrales de regalo — el MISMO contrato que BOGOS
  // (verificado en su gift.min.js, auditoría 2026-07-28): la app concede con
  // Σ cantidad × precio-unitario-tras-descuentos-DE-LÍNEA (su sca_fg_price en
  // esta tienda es 'discounted_price'), SIN restar cupones de pedido. Antes la
  // barra usaba cart.total_price, que SÍ los resta: con un cupón order-level
  // pedía comprar más para "desbloquear" un regalo que BOGOS ya había dado.
  function qmtxOfferBase(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return null;
      return cart.items.reduce(function (sum, it) {
        var unit = (it && typeof it.discounted_price === 'number') ? it.discounted_price
          : (it && typeof it.final_price === 'number') ? it.final_price
          : ((it && it.price) || 0);
        return sum + ((it && it.quantity) || 0) * unit;
      }, 0);
    } catch (e) { return null; }
  }

  if (customElements.get('qmtx-cart-coupon')) return;

  function qmtxMoney(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        var fmt = (window.theme && theme.moneyFormat) || (window.Shopify && Shopify.money_format);
        if (fmt) return Shopify.formatMoney(cents, fmt);
      }
    } catch (e) {}
    return (cents / 100).toFixed(2).replace('.', ',') + '€';
  }

  // Every applicable discount CODE + its total effect (order-level + line-level),
  // so a product-scoped code (e.g. 10% off one item) is shown too, not just
  // order-level codes that land in cart_level_discount_applications.
  function qmtxAppliedCodes(cart) {
    var map = {};
    (cart.discount_codes || []).forEach(function (d) {
      if (d.applicable && d.code) map[d.code.toLowerCase()] = { code: d.code, amount: 0 };
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      var k = (d.title || '').toLowerCase();
      if (map[k]) map[k].amount += d.total_allocated_amount || 0;
    });
    (cart.items || []).forEach(function (it) {
      (it.line_level_discount_allocations || []).forEach(function (al) {
        var t = ((al.discount_application && al.discount_application.title) || '').toLowerCase();
        if (map[t]) map[t].amount += al.amount || 0;
      });
    });
    return Object.keys(map).map(function (k) { return map[k]; });
  }

  class QmtxCartCoupon extends HTMLElement {
    connectedCallback() {
      // `qmtx_discount`, NUNCA `discount`: este campo vive dentro del form
      // POST /cart del carrito/drawer y con ese nombre viajaba VACIO en el
      // submit de "Finalizar compra", borrandole el cupon al cliente justo
      // al ir a pagar (ver cart-coupon.liquid). Se acepta el nombre viejo por
      // si queda HTML cacheado de una version anterior.
      this.input = this.querySelector('[name="qmtx_discount"]') || this.querySelector('[name="discount"]');
      this.applyBtn = this.querySelector('[data-coupon-apply]');
      this.chipsSlot = this.querySelector('[data-coupon-chips]');
      this.isDrawer = !!this.closest('.m-cart-drawer');

      if (this.applyBtn) {
        this._onApply = this.apply.bind(this);
        this.applyBtn.addEventListener('click', this._onApply);
      }
      if (this.input) {
        this._onKey = function (e) {
          if (e.key === 'Enter') { e.preventDefault(); this.apply(e); }
        }.bind(this);
        this.input.addEventListener('keydown', this._onKey);
      }
      this._onRemove = this.remove.bind(this);
      // keep the applied-code chips in sync with any cart change
      this._onCartUpdate = function (e) {
        var cart = e && e.detail && e.detail.cart;
        if (cart) this.renderChips(cart); else this.refreshChips();
      }.bind(this);
      document.addEventListener('cart:updated', this._onCartUpdate);

      this.refreshChips();

      // Confirm + flash after an apply (survives the reload / re-render).
      try {
        var flag = sessionStorage.getItem(FLASH_KEY);
        if (flag) {
          sessionStorage.removeItem(FLASH_KEY);
          if (flag !== '__removed__') this.setFeedback('applied');
          this.flashDiscounts();
        }
      } catch (e) {}
    }

    disconnectedCallback() {
      if (this.applyBtn && this._onApply) this.applyBtn.removeEventListener('click', this._onApply);
      if (this.input && this._onKey) this.input.removeEventListener('keydown', this._onKey);
      if (this._onCartUpdate) document.removeEventListener('cart:updated', this._onCartUpdate);
    }

    // Por XHR y no fetch: el override de BOGOS puede tragarse la promesa en
    // plena digestión y este getCart abre TODOS los flujos del cupón.
    getCart() {
      return new Promise(function (resolve, reject) {
        qmtxXhrJson(qmtxQuiet(shopRoot() + 'cart.js?qmtx=' + Date.now()), function (c) {
          if (c) resolve(c); else reject(new Error('cart fetch failed'));
        });
      });
    }
    // The section IDs the currently-open cart component(s) need repainted, per the
    // theme's own bundling event (e.g. 'cart-drawer' when the drawer is open).
    getSections() {
      var sections = [];
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
        );
      } catch (e) {}
      // Always include the section this coupon lives in (the drawer's
      // <m-cart-items data-section-name="cart-drawer">), so the repaint works
      // even if no component registered via the event above.
      var host = this.closest('[data-section-name]');
      var name = host && host.getAttribute('data-section-name');
      if (name && sections.indexOf(name) === -1) sections.push(name);
      return sections;
    }

    // POST /cart/update.js DOES honour the `sections` param (GET /cart.js does not),
    // so we get the freshly-rendered drawer/cart HTML back and can repaint in place.
    // Returns the parsed cart (with .sections).
    setDiscount(code) {
      var body = { discount: code };
      var sections = this.getSections();
      if (sections.length) { body.sections = sections; } // matches theme's cart/change.js call (array, no sections_url)
      return fetch(shopRoot() + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    }

    refreshChips() {
      var self = this;
      this.getCart().then(function (cart) { self.renderChips(cart); }).catch(function () {});
    }

    renderChips(cart) {
      if (!this.chipsSlot) return;
      var codes = qmtxAppliedCodes(cart);
      if (!codes.length) { this.chipsSlot.innerHTML = ''; return; }
      var removeLabel = this.chipsSlot.getAttribute('data-remove-label') || 'Quitar';
      var tag = '<svg viewBox="0 0 12 13" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 .5h3a2 2 0 0 1 2 2v3a.995.995 0 0 1-.293.707l-6 6a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414l6-6A.995.995 0 0 1 7 .5zm2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0z" fill="currentColor"></path></svg>';
      var x = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      this.chipsSlot.innerHTML = codes.map(function (c) {
        var amount = c.amount > 0 ? '<span class="qmtx-coupon__chip-amount">-' + qmtxMoney(c.amount) + '</span>' : '';
        return '<li class="qmtx-coupon__chip">' + tag +
          '<span class="qmtx-coupon__chip-code">' + c.code + '</span>' + amount +
          '<button type="button" class="qmtx-coupon__remove" data-coupon-remove data-code="' + c.code + '" aria-label="' + removeLabel + '">' + x + '</button></li>';
      }).join('');
      var self = this;
      this.chipsSlot.querySelectorAll('[data-coupon-remove]').forEach(function (b) {
        b.addEventListener('click', self._onRemove);
      });
    }

    // Doble vía + desatasco (auditoría 2026-07-28): BOGOS overrida window.fetch
    // y en plena digestión de regalos puede TRAGARSE la promesa del POST — el
    // código quedaba aplicado en servidor con "Aplicar" pillado para siempre y
    // sin feedback. Mismo patrón que el watchdog del upsell: la promesa del
    // fetch y una verificación por XHR compiten, gana la primera y el botón
    // SIEMPRE se libera (desatasco absoluto a los 6 s).
    apply(e) {
      if (e) e.preventDefault();
      var code = (this.input.value || '').trim();
      if (!code) { this.input.focus(); return; }
      var self = this;
      this.setBusy(true);
      this.setFeedback('');

      var settled = false;
      var codeMatches = function (d, mustApply) {
        return d && d.code && d.code.toLowerCase() === code.toLowerCase() && (!mustApply || d.applicable);
      };
      var finishOk = function (cart) {
        if (settled) return; settled = true;
        try { sessionStorage.setItem(FLASH_KEY, code); } catch (err) {}
        self.setBusy(false); // antes del repintado: jamás un botón pillado
        self.refresh(cart);
      };
      var finishInvalid = function (restored) {
        if (settled) return; settled = true;
        self.setBusy(false);
        self.setFeedback('invalid');
        if (restored) self.renderChips(restored);
      };
      var finishError = function () {
        if (settled) return; settled = true;
        self.setBusy(false);
        self.setFeedback('error');
      };

      // Recordar el código aplicado AHORA para poder restaurarlo si el nuevo
      // no es válido. Si esta lectura falla, seguimos con prev vacío.
      qmtxXhrJson(qmtxQuiet(shopRoot() + 'cart.js?qmtx=' + Date.now()), function (before) {
        var prev = ((before && before.discount_codes) || []).filter(function (d) { return d.applicable; })[0];
        var prevCode = prev ? prev.code : '';

        // Verificación por XHR de la VERDAD del carrito. decisive=false solo
        // confirma el éxito (sin riesgo de falso error); decisive=true también
        // concluye el fallo.
        var verify = function (decisive) {
          if (settled) return;
          qmtxXhrJson(qmtxQuiet(shopRoot() + 'cart.js?qmtx=' + Date.now()), function (c) {
            if (settled) return;
            if (!c) { if (decisive) finishError(); return; }
            var applied = (c.discount_codes || []).some(function (d) { return codeMatches(d, true); });
            if (applied) {
              // Éxito sin la respuesta del POST → faltan las sections: se piden
              // por XHR (el transporte del puente) y se repinta igual.
              qmtxXhrJson(qmtxQuiet(shopRoot() + '?sections=' + encodeURIComponent(self.getSections().join(','))), function (secs) {
                if (secs) c.sections = secs;
                finishOk(c);
              });
            } else if (decisive) {
              var present = (c.discount_codes || []).some(function (d) { return codeMatches(d, false); });
              if (present) {
                // Llegó al servidor pero no aplica → restaurar el anterior.
                self.setDiscount(prevCode).catch(function () {});
                finishInvalid(c);
              } else {
                finishError();
              }
            }
          });
        };

        self.setDiscount(code)                     // apply/replace → returns cart
          .then(function (after) {
            if (settled) return;
            var ok = (after.discount_codes || []).some(function (d) { return codeMatches(d, true); });
            if (ok) {
              finishOk(after);
            } else {
              // rejected → put back the previous valid code (or clear the rejected one)
              return self.setDiscount(prevCode).then(function (restored) {
                finishInvalid(restored);
              });
            }
          })
          .catch(function () { if (!settled) verify(true); });

        setTimeout(function () { if (!settled) verify(false); }, 900);
        setTimeout(function () { if (!settled) verify(false); }, 2200);
        setTimeout(function () { if (!settled) verify(true); }, 4000);
        setTimeout(function () { finishError(); }, 6000);
      });
    }

    remove(e) {
      if (e) e.preventDefault();
      var self = this;
      this.setBusy(true);
      this.setFeedback('');

      var settled = false;
      var finishOk = function (cart) {
        if (settled) return; settled = true;
        try { sessionStorage.setItem(FLASH_KEY, '__removed__'); } catch (err) {}
        self.setBusy(false);
        self.refresh(cart);
      };
      var finishError = function () {
        if (settled) return; settled = true;
        self.setBusy(false);
        self.setFeedback('error');
      };
      var verify = function (decisive) {
        if (settled) return;
        qmtxXhrJson(qmtxQuiet(shopRoot() + 'cart.js?qmtx=' + Date.now()), function (c) {
          if (settled) return;
          if (!c) { if (decisive) finishError(); return; }
          var gone = !(c.discount_codes || []).some(function (d) { return d.applicable; });
          if (gone) {
            qmtxXhrJson(qmtxQuiet(shopRoot() + '?sections=' + encodeURIComponent(self.getSections().join(','))), function (secs) {
              if (secs) c.sections = secs;
              finishOk(c);
            });
          } else if (decisive) {
            finishError();
          }
        });
      };

      this.setDiscount('').then(function (cart) {
        if (!settled) finishOk(cart);
      }).catch(function () { if (!settled) verify(true); });

      setTimeout(function () { if (!settled) verify(false); }, 900);
      setTimeout(function () { if (!settled) verify(false); }, 2200);
      setTimeout(function () { if (!settled) verify(true); }, 4000);
      setTimeout(function () { finishError(); }, 6000);
    }

    // Repaint the drawer in place from the rendered sections we just fetched, using
    // the theme's own pub/sub: <m-cart-items>.onCartUpdate reads cart.sections[name]
    // and viewTransitions #cart-drawer-form + footer (savings, subtotal, chips all
    // live inside it). Falls back to a reload on the cart page or if sections are
    // missing. (The old window.theme.onCartDrawerUpdate refetched GET /cart.js, which
    // silently drops the `sections` param → the drawer never repainted.)
    refresh(cart) {
      var canRepaint = this.isDrawer && cart && cart.sections &&
        window.MinimogEvents && window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents && window.MinimogTheme.pubSubEvents.cartUpdate;
      if (canRepaint) {
        this.renderChips(cart); // instant chip update in case this node isn't replaced
        try {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart: cart });
        } catch (e) {}
        // also notify our own DOM-event listeners (progress bar, etc.)
        try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } })); } catch (e) {}
      } else {
        window.location.reload();
      }
    }

    setBusy(on) {
      if (this.applyBtn) this.applyBtn.disabled = !!on;
      this.classList.toggle('is-loading', !!on);
    }

    setFeedback(state) {
      this.classList.remove('is-applied', 'is-invalid', 'is-error');
      if (state === 'applied') this.classList.add('is-applied');
      else if (state === 'invalid') this.classList.add('is-invalid');
      else if (state === 'error') this.classList.add('is-error');
    }

    flashDiscounts() {
      var scope = this.closest('.m-cart-drawer, .qmtx-cart-page') || document;
      var list = scope.querySelector('.qmtx-coupon__active, .qmtx-discounts');
      if (list) { list.classList.remove('qmtx-flash'); void list.offsetWidth; list.classList.add('qmtx-flash'); }
    }
  }

  customElements.define('qmtx-cart-coupon', QmtxCartCoupon);

  /**
   * <qmtx-shipping-estimator> — inline "Envío" card on the cart page.
   * Reuses Shopify's classic shipping-rates endpoint (same as Minimog's
   * cart-addon.js), so no new cart logic is introduced.
   */
  if (!customElements.get('qmtx-shipping-estimator')) {
    class QmtxShippingEstimator extends HTMLElement {
      connectedCallback() {
        this.country = this.querySelector('#qmtx-ship-country');
        this.province = this.querySelector('#qmtx-ship-province');
        this.provinceWrap = this.querySelector('#qmtx-ship-province-wrap');
        this.zip = this.querySelector('#qmtx-ship-zip');
        this.calcBtn = this.querySelector('[data-shipping-calc]');
        this.rates = this.querySelector('[data-shipping-rates]');

        if (window.Shopify && Shopify.CountryProvinceSelector) {
          try {
            new Shopify.CountryProvinceSelector('qmtx-ship-country', 'qmtx-ship-province', {
              hideElement: 'qmtx-ship-province-wrap'
            });
          } catch (e) {
            if (this.provinceWrap) this.provinceWrap.style.display = 'none';
          }
        } else if (this.provinceWrap) {
          this.provinceWrap.style.display = 'none';
        }

        if (this.calcBtn) {
          this._onCalc = this.calc.bind(this);
          this.calcBtn.addEventListener('click', this._onCalc);
        }
      }

      disconnectedCallback() {
        if (this.calcBtn && this._onCalc) this.calcBtn.removeEventListener('click', this._onCalc);
      }

      calc(e) {
        if (e) e.preventDefault();
        if (!this.rates) return;
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        var params = new URLSearchParams();
        params.set('shipping_address[country]', this.country ? this.country.value : '');
        if (this.province && this.province.value) {
          params.set('shipping_address[province]', this.province.value);
        }
        params.set('shipping_address[zip]', this.zip ? (this.zip.value || '').trim() : '');

        this.rates.classList.remove('qmtx-shipping__rates--error');
        this.rates.innerHTML = '…';

        fetch(qmtxQuiet(root + 'cart/shipping_rates.json?' + params.toString()), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
            if (res && res.shipping_rates) {
              if (!res.shipping_rates.length) {
                var none = (window.MinimogStrings && MinimogStrings.noShippingRate) || 'No hay tarifas para esta dirección.';
                this.rates.innerHTML = '<p>' + none + '</p>';
                return;
              }
              this.rates.innerHTML = res.shipping_rates.map(function (rt) {
                return '<div class="qmtx-rate"><span>' + rt.name + '</span><strong>' + rt.price + ' ' + cur + '</strong></div>';
              }).join('');
            } else {
              this.rates.classList.add('qmtx-shipping__rates--error');
              var msg = '';
              try {
                var first = Object.values(res)[0];
                msg = Array.isArray(first) ? first[0] : first;
              } catch (err) {}
              this.rates.innerHTML = '<p>' + (msg || 'No se pudo calcular el envío.') + '</p>';
            }
          }.bind(this))
          .catch(function () {
            this.rates.classList.add('qmtx-shipping__rates--error');
            this.rates.innerHTML = '<p>No se pudo calcular el envío.</p>';
          }.bind(this));
      }
    }
    customElements.define('qmtx-shipping-estimator', QmtxShippingEstimator);
  }

  /**
   * <qmtx-progress> — our own gift/free-shipping progress bar (replaces BOGOS's
   * app-block bar, which can't be embedded on the cart's Liquid template).
   * Purely visual; BOGOS still adds the gifts. Progress is by cart value.
   *
   * v2 — "valor de regalos" (auto desde BOGOS):
   *   · Fuente auto: lee window.fgData.offers (el config público que BOGOS carga
   *     en cada página) → umbrales, valor real de cada regalo y su foto. Cuando
   *     cambian los regalos en el admin de BOGOS, la barra se actualiza sola.
   *   · Los hitos conseguidos se colapsan en una pila a la izquierda y la barra
   *     se re-escala hasta el último tramo (goal-gradient: el siguiente objetivo
   *     se ve más cerca y no se acumulan puntitos).
   *   · Mensaje de valor: "Añade 4,10€ → +13,40€ en regalos" + chip acumulado
   *     "Ya llevas X€ en regalos GRATIS" (valor real de los regalos del carrito).
   *   · Fallback: si fgData no está (script bloqueado / app desinstalada) se usan
   *     los objetivos manuales de Ajustes del tema, como hasta ahora.
   */
  function qmtxIcon(name) {
    if (name === 'truck') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h10.5v8.5H3zM13.5 9.5H17l3.5 3v2.5h-7z"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="16.5" cy="17.5" r="1.6"/></svg>';
    }
    if (name === 'star') {
      return '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9L6.7 19.5l1.1-6L3.4 9.3l6-.8z"/></svg>';
    }
    if (name === 'trophy') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 21h8M12 17v4M7 4h10v6a5 5 0 0 1-10 0zM7 5H4v2a3 3 0 0 0 3 3M17 5h3v2a3 3 0 0 1-3 3"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v8H4v-8M2 7h20v5H2zM12 20V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
  }

  var QMTX_CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12.5l5 5 10-11"/></svg>';
  // Glyphs de ROL (stroke currentColor → heredan el color del contexto): el
  // REGALO marca el premio, el CARRITO marca el umbral de gasto (solo el
  // siguiente). Distinguen "lo que te llevas" de "lo que gastas" sin usar
  // color (que está dosificado).
  var QMTX_GIFT_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v8H4v-8M2 7h20v5H2zM12 20V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
  var QMTX_CART_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 3.5h2.4l1.9 10.1a1.6 1.6 0 0 0 1.6 1.3h7.9a1.6 1.6 0 0 0 1.6-1.2l1.4-6.7H6.2"/><circle cx="9" cy="19.5" r="1.4"/><circle cx="17" cy="19.5" r="1.4"/></svg>';

  function qmtxTpl(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] != null ? map[k] : m;
    });
  }

  // Códigos de oferta-por-link ACTIVOS: BOGOS los persiste al entrar por
  // ?freegifts_code=X en la cookie sca_fg_codes=["X",...] (su misma fuente
  // de verdad → imposible desincronizarse de lo que BOGOS va a regalar).
  function qmtxFgCodes() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)sca_fg_codes=([^;]*)/);
      if (!m) return [];
      var arr = JSON.parse(decodeURIComponent(m[1]));
      if (!arr || typeof arr.map !== 'function') return [];
      return arr.map(function (c) { return String(c).toUpperCase(); });
    } catch (e) { return []; }
  }

  // Medición: GA4 (gtag o dataLayer/GTM) + bus de eventos de Shopify
  // (Customer Events — cualquier píxel del admin puede suscribirse). Nunca rompe.
  function qmtxTrack(name, params) {
    var p = params || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, p);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, p));
      }
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, p);
      }
    } catch (e) {}
  }

  // Atributo de carrito → viaja al PEDIDO (Admin → pedido → detalles
  // adicionales y exports CSV). Fire-and-forget.
  //
  // ⚠️ Cada POST a /cart/update.js dispara el observer de BOGOS
  // (`cart-events observer:/cart/update.js`) → checkEventCart → refreshCart →
  // renderCart → repintado del drawer → nos vuelven a construir → si
  // reenviásemos el atributo, bucle infinito (incidente 2026-07-20).
  // Por eso: DEDUPE por valor (el mismo dato jamás se manda dos veces) y
  // TOPE duro de envíos por carga de página. Es telemetría de pedido, no
  // vale la pena arriesgar el carrito por ella.
  // El dedupe vive en sessionStorage y NO en memoria: si viviera en memoria,
  // una recarga lo borraría y el patrón "POST → BOGOS reprocesa → recarga →
  // POST" se convertiría en un bucle de recargas del /cart.
  var qmtxAttrPosts = 0;
  var QMTX_ATTR_MAX = 4;
  function qmtxAttrSeen(k, v) {
    try {
      var key = 'qmtx_attr_' + k;
      if (sessionStorage.getItem(key) === v) return true;
      sessionStorage.setItem(key, v);
      return false;
    } catch (e) { return false; }
  }
  function qmtxCartAttr(attrs) {
    if (qmtxB2BCart) return;
    try {
      var pending = {};
      var any = false;
      Object.keys(attrs || {}).forEach(function (k) {
        var v = String(attrs[k]);
        if (qmtxAttrSeen(k, v)) return;
        pending[k] = attrs[k];
        any = true;
      });
      if (!any || qmtxAttrPosts >= QMTX_ATTR_MAX) return;
      qmtxAttrPosts++;
      // Marcado como invisible para BOGOS: solo escribe un atributo de pedido,
      // no cambia nada que afecte a los regalos. El propio BOGOS marca sus
      // escrituras de atributos con `sca_attr_cart`, que su observer también
      // excluye — mismo criterio.
      fetch(qmtxQuiet(shopRoot() + 'cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: pending })
      }).catch(function () {});
    } catch (e) {}
  }

  if (!customElements.get('qmtx-progress')) {
    class QmtxProgress extends HTMLElement {
      connectedCallback() {
        try { this.goals = JSON.parse(this.getAttribute('data-goals') || '[]'); } catch (e) { this.goals = []; }
        this.emptyMsg = this.getAttribute('data-empty-msg') || '';
        this.unlockedMsg = this.getAttribute('data-unlocked-msg') || '';
        this.countdownTpl = this.getAttribute('data-countdown-tpl') || 'Añade {remaining} para {goal}';
        this.descTpl = this.getAttribute('data-desc-tpl') || 'Compra {value}';
        this.valueTpl = this.getAttribute('data-value-tpl') || 'Añade {remaining} y consigue {reward}';
        this.rewardGiftsTpl = this.getAttribute('data-reward-gifts-tpl') || '+{value} en regalos GRATIS';
        this.rewardChoiceTpl = this.getAttribute('data-reward-choice-tpl') || 'un regalo a elegir (hasta {value})';
        this.labelGiftsTpl = this.getAttribute('data-label-gifts-tpl') || '+{value}';
        this.labelGiftsNext = this.getAttribute('data-label-gifts-next') || '+{value} GRATIS';
        this.labelChoiceTpl = this.getAttribute('data-label-choice-tpl') || '+{value} GRATIS';
        this.choiceTitle = this.getAttribute('data-choice-title') || 'Elige regalo';
        this.topTitle = this.getAttribute('data-top-title') || 'Mega regalo';
        this.clusterTpl = this.getAttribute('data-cluster-tpl') || '+{value} GRATIS';
        this.cumeTpl = this.getAttribute('data-cume-tpl') || 'Llevas {value} de {max} posibles en regalos GRATIS';
        this.cumeAllTpl = this.getAttribute('data-cume-all-tpl') || 'Todo desbloqueado · {value} en regalos GRATIS';
        this.cumeChoiceTpl = this.getAttribute('data-cume-choice-tpl') || 'Te falta elegir tu regalo';
        this.cumeTeaseTpl = this.getAttribute('data-cume-tease-tpl') || 'Puedes llevarte hasta {max} en regalos GRATIS';
        this.shipCouponTpl = this.getAttribute('data-ship-coupon-tpl') || 'Con tu cupón el pedido queda en {total}. El envío gratis es desde {threshold} después de descuentos.';
        this.codeArmedTpl = this.getAttribute('data-code-armed-tpl') || '{msg} activados con tu enlace · a partir de {min}';
        this.codeDoneTpl = this.getAttribute('data-code-done-tpl') || '{msg} incluidos';
        this.codeSavingsTpl = this.getAttribute('data-code-savings-tpl') || '{msg} adicionales';
        this.freeWord = this.getAttribute('data-free-word') || 'GRATIS';
        // "Completa tu regalo": mini-productos que cuestan ~lo que falta para
        // el siguiente tramo, con añadir directo. Solo B2C. Doble fuente:
        // recomendaciones de Shopify (los compran juntos) + colección editable
        // de básicos baratos (barritas, unidades, shaker...). Descartable (✕).
        this.upsellTitle = this.getAttribute('data-upsell-title') || 'Complétalo y desbloquea {reward}';
        this.upsellSocial = this.getAttribute('data-upsell-social') || 'Otros clientes los compran juntos';
        this._upsellOn = (this.getAttribute('data-upsell') || 'on') !== 'off';
        this._upsellMax = Math.round((parseFloat(String(this.getAttribute('data-upsell-max') || '15').replace(',', '.')) || 15) * 100);
        this._upsellSeed = parseInt(this.getAttribute('data-upsell-seed') || '0', 10) || 0;
        this._upsellCol = this.getAttribute('data-upsell-collection') || 'completa-tu-regalo';
        try { this._upsellDismissed = sessionStorage.getItem('qmtx-upsell-off') === '1'; } catch (e) { this._upsellDismissed = false; }
        // En el DRAWER el slider arranca PLEGADO a una fila fina (el carrito
        // necesita su espacio); el cliente lo abre con un tap y se recuerda
        // por sesión. En la página /cart siempre abierto (sobra sitio).
        try { this._upsellOpen = sessionStorage.getItem('qmtx-upsell-open') === '1'; } catch (e) { this._upsellOpen = false; }
        this._auto = (this.getAttribute('data-source') || 'auto') === 'auto';
        // "20; 39,90" → [20, 39.9]: normaliza la coma decimal ANTES de separar.
        this._ignore = (this.getAttribute('data-ignore') || '')
          .replace(/(\d),(\d)/g, '$1.$2')
          .split(/[,;\s]+/).map(function (s) { return parseFloat(s); })
          .filter(function (n) { return !isNaN(n); });
        this._tags = (this.getAttribute('data-customer-tags') || '')
          .split(',').map(function (s) { return s.trim().toLowerCase(); })
          .filter(Boolean);
        if (this._tags.indexOf('b2b') !== -1 || this._tags.indexOf('influencer') !== -1) this._upsellOn = false;

        // Valor de los regalos YA en el carrito: primer render desde Liquid
        // (compare_at de las líneas bogos-gift); después se recalcula en JS
        // con el mapa variante→valor de fgData.
        this._giftTotal = parseInt(this.getAttribute('data-gift-total') || '0', 10) || 0;
        this._giftMap = null;
        this._prevReached = null; // para el confetti al desbloquear
        this._nudged = false;     // empujoncito del slider, una vez

        // En espacios estrechos (drawer siempre; carrito en móvil) los tramos
        // más allá de los 2 siguientes se COLAPSAN en un racimo a la derecha.
        // Inicializar ANTES del primer render.
        this._isDrawer = !!this.closest('.m-cart-drawer');
        this._mql = window.matchMedia ? window.matchMedia('(max-width: 700px)') : null;

        this.tiers = null; // tiers auto (BOGOS); null → fallback a this.goals
        this._buildAuto();
        // En modo auto seguimos SIEMPRE aunque aún no haya tiers: fgData suele
        // llegar tarde (script diferido de la app) y el reintento de abajo lo
        // recoge. Salir aquí dejaría la barra en blanco para toda la sesión.
        if (!this._auto && !this.tiers && !this.goals.length) return;

        this._lastCart = null;
        // Lo que restan los cupones según Liquid: solo para el primer render
        // (sin /cart.js aún). Luego manda el cart real (ver shipByCoupon).
        this._seedCodeCut = parseInt(this.getAttribute('data-code-cut') || '0', 10) || 0;
        this.render(parseInt(this.getAttribute('data-cart-total') || '0', 10) || 0, null);

        // fgData puede cargar después que nosotros (script de la app diferido):
        // reintenta un rato y re-renderiza cuando aparezca.
        var self = this;
        if (this._auto && !this.tiers) {
          var tries = 0;
          this._retryTimer = setInterval(function () {
            tries++;
            self._buildAuto();
            if (self.tiers) {
              clearInterval(self._retryTimer); self._retryTimer = null;
              // Los tramos REALES acaban de aparecer: el primer pintado usó el
              // fallback manual, así que el salto de hitos conseguidos NO es un
              // desbloqueo del cliente, es que ahora sabemos la verdad. Se
              // rebobina la línea base para no lanzar confeti ni el POST del
              // atributo de pedido (ese POST despierta al observer de BOGOS y
              // en /cart acababa en recarga tras recarga — 2026-07-20).
              self._prevReached = null;
              self.render(self._lastTotal || 0, self._lastCart);
            } else if (tries >= 20) { // ~10s y desistimos: fallback manual
              clearInterval(self._retryTimer); self._retryTimer = null;
            }
          }, 500);
        }

        var t;
        var lastFetchAt = 0;
        var MIN_GAP = 500; // techo de 2 peticiones/s: nunca martillear /cart.js
        // Authoritative refresh from /cart.js (debounced). The cart is the single
        // source of truth regardless of which event/mechanism fired. Guard by
        // FIRMA total+item_count (no solo total): los regalos de BOGOS son líneas
        // a 0€, añadirlos/quitarlos no cambia el total pero SÍ el item_count — y
        // el chip "ya llevas X€" debe reaccionar. La firma también evita que
        // nuestro propio re-render (via MutationObserver) entre en bucle.
        //
        // El MutationObserver del drawer nos llama en CADA repintado, venga de
        // quien venga (BOGOS, el tema, otra app). Si algo repinta en ráfaga, el
        // debounce solo agrupa 100 ms: por eso además se APLAZA (no se descarta)
        // la petición hasta cumplir MIN_GAP. En el incidente del 2026-07-20 esto
        // fue lo que convirtió un bucle en una lluvia de 429 sobre /cart.js.
        this._refresh = function () {
          clearTimeout(t);
          var wait = Math.max(100, MIN_GAP - (Date.now() - lastFetchAt));
          t = setTimeout(function () {
            lastFetchAt = Date.now();
            fetch(qmtxQuiet(shopRoot() + 'cart.js'), { headers: { 'Accept': 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (c) {
                if (!c || typeof c.total_price !== 'number') return;
                var sig = c.total_price + ':' + c.item_count;
                var base = qmtxOfferBase(c);
                if (sig !== self._lastSig || !self._lastCart) self.render(base != null ? base : c.total_price, c);
              })
              .catch(function () {});
          }, wait);
        };

        // 1) DOM cart events (fired on the cart PAGE + some theme flows). Use the
        //    event's cart total when present, else fall back to a /cart.js refresh.
        this._onCart = function (e) {
          var c = e && e.detail && e.detail.cart;
          if (c && typeof c.total_price === 'number') {
            var base = qmtxOfferBase(c);
            self.render(base != null ? base : c.total_price, c.items ? c : self._lastCart);
          } else self._refresh();
        };
        this._domEvents = ['cart:updated', 'cart:refresh', 'cart:changed', 'minimog:cart:update', 'minimog:cart:refresh'];
        this._domEvents.forEach(function (n) { document.addEventListener(n, self._onCart); });

        // 2) The reliable trigger for a DRAWER add/qty change: Minimog re-renders
        //    the cart body server-side (it emits an INTERNAL pub/sub event, NOT a
        //    DOM 'cart:updated'). Watch the cart containers for that re-render and
        //    resync from /cart.js — same pattern b2b.js uses for the drawer.
        this._mo = null;
        if (window.MutationObserver) {
          var roots = ['MinimogCartDrawer', 'MinimogCart']
            .map(function (id) { return document.getElementById(id); })
            .filter(Boolean);
          if (roots.length) {
            // _selfPaint evita el eco de nuestro propio innerHTML (y su fetch).
            this._mo = new MutationObserver(function () { if (!self._selfPaint) self._refresh(); });
            roots.forEach(function (r) { self._mo.observe(r, { childList: true, subtree: true }); });
          }
        }

        // Resaltar el objetivo COMPLETO (hito + su etiqueta) al pasar el ratón
        // o tocar: el ciclo se CONGELA mostrando las FOTOS del regalo y los
        // textos pasan a color vivo (clase .is-hot en el par [data-gi]).
        this._setHot = function (gi, on) {
          self.querySelectorAll('[data-gi]').forEach(function (n) {
            if (on && n.getAttribute('data-gi') === gi) n.classList.add('is-hot');
            else n.classList.remove('is-hot');
          });
        };
        // El racimo de futuros se EXPANDE al pasar el ratón o tocarlo (6s),
        // para ver cada hito por separado sin aglutinar texto. Render DIFERIDO
        // un tick: repintar dentro del click saca el nodo pulsado del DOM y el
        // drawer lo interpreta como "click fuera" → se cerraría entero.
        this._openCluster = function () {
          if (self._clusterOpen) return;
          self._clusterOpen = true;
          qmtxTrack('pb_cluster_expand', { cart_total_eur: (self._lastTotal || 0) / 100 });
          setTimeout(function () { self.render(self._lastTotal || 0, self._lastCart); }, 0);
          clearTimeout(self._clusterTimer);
          self._clusterTimer = setTimeout(function () {
            self._clusterOpen = false;
            self.render(self._lastTotal || 0, self._lastCart);
          }, 6000);
        };
        this._onOver = function (e) {
          var c = e.target && e.target.closest ? e.target.closest('.qmtx-progress__goal--cluster') : null;
          if (c && self.contains(c)) { self._openCluster(); return; }
          var hit = e.target && e.target.closest ? e.target.closest('[data-tip]') : null;
          if (hit && self.contains(hit)) self._placeTip(hit);
          var g = e.target && e.target.closest ? e.target.closest('.qmtx-progress__goal[data-gi]') : null;
          if (g && self.contains(g)) self._setHot(g.getAttribute('data-gi'), true);
        };
        this._onOut = function (e) {
          var g = e.target && e.target.closest ? e.target.closest('.qmtx-progress__goal[data-gi]') : null;
          if (!g || !self.contains(g)) return;
          if (e.relatedTarget && g.contains(e.relatedTarget)) return;
          if (g.classList.contains('is-tipped')) return; // fijado por tap
          self._setHot(null, false);
        };
        this.addEventListener('mouseover', this._onOver);
        this.addEventListener('mouseout', this._onOut);

        // El ciclo de atención (icono↔fotos, 8s) se reproduce UNA vez por
        // carga: al terminar queda la CALMA (fotos fijas) — los repintados
        // posteriores (racimo, upsell, cantidades) ya no relanzan el ciclo.
        this._onSwapEnd = function (e) {
          if (e.animationName === 'qmtx-pb-swap-a') self.classList.add('qmtx-progress--calm');
        };
        this.addEventListener('animationend', this._onSwapEnd);

        // Pulso de atención en "Complétalo y desbloquea…" al ABRIR el sidecart:
        // dos latidos suaves de tinte azulado en la cabecera plegada, 400ms
        // después de abrirse (deja asentar el slide-in) y REPOSO — one-shot,
        // jamás en bucle. Observa solo la clase del drawer: cero eco con el
        // MutationObserver del carrito (que escucha childList).
        var drawerEl = this.closest('.m-cart-drawer');
        if (drawerEl && window.MutationObserver) {
          this._drawerWasOpen = drawerEl.classList.contains('m-cart-drawer--active');
          this._moDrawer = new MutationObserver(function () {
            var open = drawerEl.classList.contains('m-cart-drawer--active');
            if (open && !self._drawerWasOpen) {
              clearTimeout(self._pulseT);
              self._pulseT = setTimeout(function () {
                var head = self.querySelector('.qmtx-progress__upsell--collapsed .qmtx-progress__upsell-head');
                if (!head) return;
                head.classList.remove('qmtx-progress__upsell-head--pulse');
                void head.offsetWidth; // reinicia el one-shot si reabre rápido
                head.classList.add('qmtx-progress__upsell-head--pulse');
                self._pulseT = setTimeout(function () {
                  var h = self.querySelector('.qmtx-progress__upsell-head--pulse');
                  if (h) h.classList.remove('qmtx-progress__upsell-head--pulse');
                }, 1900);
              }, 400);
            }
            self._drawerWasOpen = open;
          });
          this._moDrawer.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
        }

        // Tooltip por click/tap (en móvil no hay hover): alterna .is-tipped en
        // el regalo pulsado; se cierra tocando fuera o pulsando otro.
        this._onTap = function (e) {
          var c = e.target && e.target.closest ? e.target.closest('.qmtx-progress__goal--cluster') : null;
          if (c && self.contains(c)) { self._openCluster(); return; }
          var hit = e.target && e.target.closest ? e.target.closest('[data-tip]') : null;
          if (hit && self.contains(hit)) {
            var was = hit.classList.contains('is-tipped');
            self.querySelectorAll('.is-tipped').forEach(function (x) { x.classList.remove('is-tipped'); });
            if (!was) { hit.classList.add('is-tipped'); self._placeTip(hit); }
            var gi = hit.getAttribute('data-gi');
            if (gi != null) self._setHot(gi, !was); else self._setHot(null, false);
          } else {
            self.querySelectorAll('.is-tipped').forEach(function (x) { x.classList.remove('is-tipped'); });
            self._setHot(null, false);
          }
        };
        document.addEventListener('click', this._onTap);

        // Añadir directo desde la tarjeta del upsell. El repintado del carrito
        // usa el mismo puente que BOGOS (bogos-theme-bridge → pub/sub del tema).
        this._onAdd = function (e) {
          // Un arrastre no es un click: si el puntero se movió, no añadimos.
          if (self._dragMoved > 6) { self._dragMoved = 0; return; }
          // Plegar/desplegar el slider en el drawer (se recuerda por sesión).
          // OJO: el re-render va DIFERIDO un tick — si repintamos dentro del
          // click, el botón pulsado sale del DOM antes de que el listener
          // "click fuera = cerrar" del drawer lo compruebe, y el drawer se
          // cerraba entero (bug reportado por Toti).
          var tg = e.target && e.target.closest ? e.target.closest('[data-upsell-toggle]') : null;
          if (tg && self.contains(tg)) {
            e.preventDefault();
            self._upsellOpen = !self._upsellOpen;
            try { sessionStorage.setItem('qmtx-upsell-open', self._upsellOpen ? '1' : '0'); } catch (err) {}
            qmtxTrack(self._upsellOpen ? 'pb_upsell_expand' : 'pb_upsell_collapse', {});
            setTimeout(function () { self.render(self._lastTotal || 0, self._lastCart); }, 0);
            return;
          }
          var x = e.target && e.target.closest ? e.target.closest('[data-upsell-close]') : null;
          if (x && self.contains(x)) {
            e.preventDefault();
            self._upsellDismissed = true;
            try { sessionStorage.setItem('qmtx-upsell-off', '1'); } catch (err) {}
            qmtxTrack('pb_upsell_dismiss', { remaining_eur: self._upsellCtx ? self._upsellCtx.remaining / 100 : undefined });
            setTimeout(function () { self.render(self._lastTotal || 0, self._lastCart); }, 0);
            return;
          }
          var btn = e.target && e.target.closest ? e.target.closest('[data-add-id]') : null;
          if (!btn || !self.contains(btn)) return;
          e.preventDefault();
          if (qmtxB2BCart) return; // B2B cart behind a stale retail tab
          if (btn.classList.contains('is-adding')) return;
          btn.classList.add('is-adding');
          var tEl = btn.querySelector('.qmtx-progress__upsell-t');
          var pEl = btn.querySelector('.qmtx-progress__upsell-p');
          var addId = parseInt(btn.getAttribute('data-add-id'), 10);
          // OJO: BOGOS intercepta fetch de /cart/add.js (fetch-override) y en
          // algunas páginas (PDP) se TRAGA la promesa: el producto entra pero
          // ni .then ni .catch corren → botón pillado en "añadiendo" y drawer
          // sin repintar (bug reportado por Toti, reproducido en PDP+drawer).
          // Por eso el resultado se resuelve por DOS vías y gana la primera:
          // la promesa del fetch, o un watchdog que a los 1,2s consulta la
          // verdad en GET /cart.js (ese sí resuelve siempre).
          var settled = false;
          var finish = function (ok) {
            if (settled) return;
            settled = true;
            if (!ok) {
              // Feedback de fallo: el botón vuelve y la tarjeta marca en rojo
              // un instante — antes "no pasaba nada" y parecía roto.
              btn.classList.remove('is-adding');
              btn.classList.add('is-error');
              setTimeout(function () { btn.classList.remove('is-error'); }, 1600);
              return;
            }
            // El botón se libera AQUÍ mismo, sin depender del repintado (si
            // el puente de secciones fallara, que jamás quede pillado).
            btn.classList.remove('is-adding');
            qmtxTrack('pb_upsell_add', {
              item_name: tEl ? tEl.textContent : undefined,
              price_label: pEl ? pEl.textContent : undefined,
              remaining_eur: self._upsellCtx ? self._upsellCtx.remaining / 100 : undefined,
              tier_eur: self._upsellCtx ? self._upsellCtx.tier / 100 : undefined,
              currency: 'EUR'
            });
            if (window.theme && typeof window.theme.onCartDrawerUpdate === 'function') window.theme.onCartDrawerUpdate();
            else window.location.reload();
          };
          // XHR y NO fetch: BOGOS overrida window.fetch y, en plena digestión
          // del add, puede tragarse TAMBIÉN el GET de verificación (recaída
          // del pillado reportada por Toti con el watchdog v1 ya en live).
          // XMLHttpRequest queda fuera del alcance de su override.
          // decisive=false → verificación OPTIMISTA: solo confirma el éxito
          // (si aún no está, sigue esperando — verificar pronto sin riesgo de
          // falso error). decisive=true → también concluye el fallo.
          var verify = function (decisive) {
            try {
              var xhr = new XMLHttpRequest();
              xhr.open('GET', qmtxQuiet(shopRoot() + 'cart.js?qmtx=' + Date.now()), true);
              xhr.onload = function () {
                try {
                  var c = JSON.parse(xhr.responseText);
                  var added = !!(c.items && c.items.some(function (it) { return it.variant_id === addId; }));
                  if (added || decisive) finish(added);
                } catch (e) { if (decisive) finish(false); }
              };
              xhr.onerror = function () { if (decisive) finish(false); };
              xhr.send();
            } catch (e) { if (decisive) finish(false); }
          };
          fetch(shopRoot() + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: addId,
              quantity: 1,
              // Atribución Shopify: viaja al pedido (el _ lo oculta al cliente)
              properties: { _pb_origen: 'Completa tu regalo' }
            })
          })
            .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
            .then(function () { finish(true); })
            .catch(function () { if (!settled) verify(true); });
          // Watchdog escalonado: chequeos OPTIMISTAS tempranos (el add real
          // tarda ~300ms — así la UI responde en ~0,6s aunque BOGOS se trague
          // la promesa), veredicto de fallo a los 3s y desatasco ABSOLUTO a
          // los 5,2s (jamás un botón pillado para siempre).
          setTimeout(function () { if (!settled) verify(false); }, 450);
          setTimeout(function () { if (!settled) verify(false); }, 1200);
          setTimeout(function () { if (!settled) verify(true); }, 3000);
          setTimeout(function () { finish(false); }, 5200);
        };
        this.addEventListener('click', this._onAdd);

        this._loadUpsell();

        // Re-render al cruzar el breakpoint móvil (expande/colapsa el racimo).
        if (this._mql && this._mql.addEventListener) {
          this._onMql = function () { self.render(self._lastTotal || 0, self._lastCart); };
          this._mql.addEventListener('change', this._onMql);
        }

        // Arrastre del slider con RATÓN (el táctil ya desliza nativo).
        this._dragMoved = 0;
        this._onPointerDown = function (e) {
          if (e.pointerType === 'touch') return;
          var row = e.target && e.target.closest ? e.target.closest('.qmtx-progress__upsell-row') : null;
          if (!row || !self.contains(row)) return;
          // OJO: NO activar el modo arrastre todavía — se arma solo si el
          // puntero se mueve >6px. Si lo armamos en el down, el CSS quita los
          // pointer-events de las tarjetas y un CLICK normal nunca llega al
          // botón de añadir (bug reportado por Toti).
          self._drag = { row: row, x: e.clientX, sl: row.scrollLeft, armed: false };
          self._dragMoved = 0;
        };
        this._onPointerMove = function (e) {
          if (!self._drag) return;
          var dx = e.clientX - self._drag.x;
          if (!self._drag.armed) {
            if (Math.abs(dx) <= 6) return; // aún es un click
            self._drag.armed = true;
            self._drag.row.classList.add('is-dragging');
          }
          self._dragMoved = Math.max(self._dragMoved, Math.abs(dx));
          self._drag.row.scrollLeft = self._drag.sl - dx;
        };
        this._onPointerUp = function () {
          if (!self._drag) return;
          if (self._drag.armed) self._drag.row.classList.remove('is-dragging');
          self._drag = null;
          // _dragMoved se consume en el próximo click (o caduca)
          setTimeout(function () { self._dragMoved = 0; }, 250);
        };
        this.addEventListener('pointerdown', this._onPointerDown);
        document.addEventListener('pointermove', this._onPointerMove);
        document.addEventListener('pointerup', this._onPointerUp);
      }

      disconnectedCallback() {
        var self = this;
        if (this._onCart && this._domEvents) this._domEvents.forEach(function (n) { document.removeEventListener(n, self._onCart); });
        if (this._mo) this._mo.disconnect();
        if (this._retryTimer) clearInterval(this._retryTimer);
        if (this._onTap) document.removeEventListener('click', this._onTap);
        if (this._onAdd) this.removeEventListener('click', this._onAdd);
        if (this._onOver) this.removeEventListener('mouseover', this._onOver);
        if (this._onOut) this.removeEventListener('mouseout', this._onOut);
        if (this._mql && this._onMql && this._mql.removeEventListener) this._mql.removeEventListener('change', this._onMql);
        if (this._onPointerDown) this.removeEventListener('pointerdown', this._onPointerDown);
        if (this._onPointerMove) document.removeEventListener('pointermove', this._onPointerMove);
        if (this._onPointerUp) document.removeEventListener('pointerup', this._onPointerUp);
        if (this._clusterTimer) clearTimeout(this._clusterTimer);
        if (this._moDrawer) this._moDrawer.disconnect();
        if (this._pulseT) clearTimeout(this._pulseT);
      }

      /* ---- "Completa tu regalo" (upsell hacia el siguiente tramo) ---- */

      _loadUpsell() {
        if (!this._upsellOn || this._upsellLoading) return;
        this._upsellLoading = true;
        var self = this;

        function mapRelated(d) {
          // /recommendations/products.json → precios en CÉNTIMOS (número)
          return (d.products || []).filter(function (p) {
            return p && p.available && String(p.handle || '').indexOf('sca_clone') === -1 && p.variants && p.variants.length;
          }).map(function (p) {
            var v = p.variants.filter(function (x) { return x.available; })[0] || p.variants[0];
            return { pid: p.id, vid: v.id, title: p.title, src: 'related',
              price: typeof v.price === 'number' ? v.price : (typeof p.price === 'number' ? p.price : 0),
              img: p.featured_image || '' };
          });
        }
        function mapCollection(d) {
          // /collections/<handle>/products.json → precios en EUROS (string)
          return (d.products || []).filter(function (p) {
            return p && String(p.handle || '').indexOf('sca_clone') === -1 && p.variants && p.variants.length;
          }).map(function (p) {
            var v = p.variants.filter(function (x) { return x.available; })[0];
            if (!v) return null;
            return { pid: p.id, vid: v.id, title: p.title, src: 'filler',
              price: Math.round((parseFloat(v.price) || 0) * 100),
              img: (p.images && p.images[0] && p.images[0].src) || '' };
          }).filter(Boolean);
        }

        var jobs = [];
        jobs.push(this._upsellCol
          ? fetch(shopRoot() + 'collections/' + this._upsellCol + '/products.json?limit=50', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); }).then(mapCollection).catch(function () { return []; })
          : Promise.resolve([]));
        jobs.push(this._upsellSeed
          ? fetch(shopRoot() + 'recommendations/products.json?product_id=' + this._upsellSeed + '&limit=10&intent=related', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); }).then(mapRelated).catch(function () { return []; })
          : Promise.resolve([]));

        Promise.all(jobs).then(function (res) {
          self._upsellPool = res[0].concat(res[1]).filter(function (p) { return p.price > 0; });
          if (self._upsellPool.length) self.render(self._lastTotal || 0, self._lastCart);
        });
      }

      _upsellHtml(next, remaining) {
        if (!this._upsellOn || this._upsellDismissed || !next || remaining <= 0 || remaining > this._upsellMax) return '';
        var pool = this._upsellPool;
        if (!pool || !pool.length) { this._loadUpsell(); return ''; }
        var inCart = {};
        ((this._lastCart && this._lastCart.items) || []).forEach(function (it) { inCart[it.product_id] = 1; });
        var giftProds = this._giftProds || {};

        // Candidatos que DESBLOQUEAN el tramo (precio >= lo que falta), de
        // menor a mayor. Nunca productos ya en el carrito NI regalos que va a
        // recibir gratis al subir de tramo. Mezcla garantizada: al menos 1
        // básico barato de la colección + 1 recomendado real ("los compran
        // juntos") + los más ajustados del resto. Hasta 5 (el CSS enseña 3 en
        // drawer/móvil y 5 en la página del carrito de escritorio).
        var unlockers = pool.filter(function (p) {
          return !inCart[p.pid] && !giftProds[p.pid] && p.price >= remaining;
        }).sort(function (a, b) { return a.price - b.price; });
        if (!unlockers.length) return '';
        var picked = [], seen = {};
        function take(p) { if (p && !seen[p.pid]) { seen[p.pid] = 1; picked.push(p); } }
        take(unlockers.filter(function (p) { return p.src === 'filler'; })[0]);
        take(unlockers.filter(function (p) { return p.src === 'related'; })[0]);
        for (var i = 0; i < unlockers.length && picked.length < 9; i++) take(unlockers[i]);
        picked.sort(function (a, b) { return a.price - b.price; });

        this._upsellCtx = { remaining: remaining, tier: next.value }; // para la medición GA4

        // SLIDER: 3 visibles en drawer/móvil, 5 en carrito de escritorio; el
        // resto deslizando (dedo o arrastre de ratón; sin flechas). La ganancia
        // del título va en VERDE (lenguaje de regalo, como el chip).
        var title = qmtxTpl(this.upsellTitle, {
          reward: '<span class="qmtx-gain">' + this._rewardText(next) + '</span>',
          remaining: qmtxMoney(remaining)
        });

        // DRAWER plegado: solo la fila-título con chevron — el carrito respira.
        if (this._isDrawer && !this._upsellOpen) {
          return '<div class="qmtx-progress__upsell qmtx-progress__upsell--collapsed">' +
            '<button type="button" class="qmtx-progress__upsell-head" data-upsell-toggle aria-expanded="false">' +
              '<span class="qmtx-progress__upsell-head-t">' + title + '</span>' +
              '<span class="qmtx-progress__upsell-chev" aria-hidden="true"></span>' +
            '</button></div>';
        }

        var head = this._isDrawer
          ? '<button type="button" class="qmtx-progress__upsell-head qmtx-progress__upsell-head--open" data-upsell-toggle aria-expanded="true">' +
              '<span class="qmtx-progress__upsell-head-t">' + title + '</span>' +
              '<span class="qmtx-progress__upsell-chev qmtx-progress__upsell-chev--open" aria-hidden="true"></span>' +
            '</button>'
          : '<div class="qmtx-progress__upsell-title">' + title + '</div>';

        return '<div class="qmtx-progress__upsell">' +
          '<button type="button" class="qmtx-progress__upsell-close" data-upsell-close aria-label="Ocultar sugerencias">&times;</button>' +
          head +
          (this.upsellSocial ? '<div class="qmtx-progress__upsell-social">' + this.upsellSocial + '</div>' : '') +
          '<div class="qmtx-progress__upsell-wrap">' +
          '<div class="qmtx-progress__upsell-row">' + picked.map(function (p) {
            var t = String(p.title).replace(/"/g, '&quot;');
            return '<button type="button" class="qmtx-progress__upsell-card" data-add-id="' + p.vid + '" aria-label="Añadir ' + t + '">' +
              (p.img ? '<img src="' + String(p.img).replace(/"/g, '&quot;') + '" alt="" loading="lazy">' : '') +
              '<span class="qmtx-progress__upsell-t">' + p.title + '</span>' +
              '<span class="qmtx-progress__upsell-p">' + qmtxMoney(p.price) + '</span>' +
              '<span class="qmtx-progress__upsell-add" aria-hidden="true">+</span>' +
            '</button>';
          }).join('') + '</div></div></div>';
      }

      /* ---- tiers desde BOGOS (window.fgData) ---- */

      _buildAuto() {
        if (!this._auto) return;
        var fg = window.fgData;
        if (!fg || !fg.offers || !fg.offers.forEach || !fg.offers.length) return;
        var self = this, now = Date.now(), byMin = {}, giftMap = {}, giftProds = {}, sawExcluded = false;
        var linkAll = [];

        fg.offers.forEach(function (o) {
          try {
            if (!o || o.type !== 'default' || o.deleted_at) return;
            if (o.end_time && new Date(o.end_time).getTime() < now) return;

            // Mapa variante→valor SIEMPRE (también de ofertas fuera de la barra):
            // así el chip "ya llevas X€" valora cualquier regalo del carrito.
            // Y set de PRODUCTOS regalo: el upsell no debe recomendar comprar
            // algo que el cliente va a recibir GRATIS al subir de tramo.
            (o.gift_products || []).forEach(function (gp) {
              if (gp.product_shopify_id) giftProds[gp.product_shopify_id] = 1;
              if (gp.original_product_shopify_id) giftProds[gp.original_product_shopify_id] = 1;
              (gp.variants || []).forEach(function (v) {
                var cents = Math.round((parseFloat(v.original_compare_at_price) || parseFloat(v.original_price) || 0) * 100);
                if (!cents) return;
                [v.variant_shopify_id, v.original_variant_shopify_id, v.id].forEach(function (k) {
                  if (k && !giftMap[k]) giftMap[k] = cents;
                });
              });
            });

            var cond = (o.offer_conditions || []).filter(function (c) {
              return c && c.unit === 'money' && parseFloat(c.min_amount) > 0;
            })[0];
            if (!cond) return;
            var minEur = parseFloat(cond.min_amount);

            if (self._ignore.indexOf(minEur) !== -1) return;

            // Sub-condición por tags de cliente (BOGOS excluye p.ej. b2b/influencer).
            var ct = o.sub_conditions && o.sub_conditions.customer_tags;
            if (ct && ct.value_customer_tags && ct.value_customer_tags.length) {
              var listed = ct.value_customer_tags.map(function (s) { return String(s).toLowerCase(); });
              var has = self._tags.some(function (tg) { return listed.indexOf(tg) !== -1; });
              if (ct.type_customer_tags === 'exclude' && has) {
                // Oferta válida pero ESTE cliente está excluido: si acaban
                // excluidas todas, la barra queda solo con el envío gratis
                // (no el fallback manual, que enseñaría regalos que no le tocan).
                if (self._tierFromOffer(o, minEur)) sawExcluded = true;
                return;
              }
              if (ct.type_customer_tags === 'include' && !has) {
                if (self._tierFromOffer(o, minEur)) sawExcluded = true;
                return;
              }
            }

            // Ofertas POR LINK (?freegifts_code=X → cookie sca_fg_codes):
            // regalos ADICIONALES que BOGOS solo concede con el código activo.
            // NUNCA entran como tramo (decisión de Toti: los tramos no se
            // tocan) — se catalogan y su señal es la píldora con el
            // promotion_message (el "Mensaje del carrito" de BOGOS).
            var lk = o.sub_conditions && o.sub_conditions.specific_link_address;
            if (lk) {
              // gval = valor de sus regalos (para el desglose "adicionales"
              // del bloque de ahorros); _tierFromOffer ya sabe calcularlo.
              var lt = self._tierFromOffer(o, minEur);
              linkAll.push({
                code: String(lk).toUpperCase(),
                msg: o.promotion_message || o.title || o.offer_name || '',
                min: Math.round(minEur * 100),
                gval: (lt && lt.gval) || 0
              });
              return;
            }

            var tier = self._tierFromOffer(o, minEur);
            if (!tier) return;
            var prev = byMin[minEur];
            // Ofertas influencer clonan el mismo umbral: nos quedamos con la titulada.
            if (!prev || (!prev.hasTitle && tier.hasTitle)) byMin[minEur] = tier;
          } catch (e) {}
        });

        this._giftMap = giftMap; // útil aunque no salga ningún tramo (chip acumulado)
        this._giftProds = giftProds;
        this._linkAll = linkAll; // ofertas-por-link (píldoras si su código está activo)

        var tiers = Object.keys(byMin).map(function (k) { return byMin[k]; });
        // Sin ningún tramo construible NI oferta excluida por tags: fgData está
        // pero no lo entendemos (p.ej. la app cambió su formato) → dejamos
        // this.tiers en null para que actúe el fallback manual de los ajustes.
        if (!tiers.length && !sawExcluded) return;

        // Hitos no-regalo de los ajustes (envío gratis = icono camión) se conservan.
        this.goals.forEach(function (g) {
          if (g.icon === 'truck') tiers.push({ value: g.value, kind: 'ship', title: g.title, icon: g.icon });
        });
        tiers.sort(function (a, b) { return a.value - b.value; });
        // Tramos "a elegir" → icono en vez de foto (estrella; trofeo si es el
        // último = el premio gordo). El tooltip enseña qué regalos hay.
        tiers.forEach(function (t) { if (t.choice) t.icon = 'star'; });
        var top = tiers[tiers.length - 1];
        if (top && top.choice) top.icon = 'trophy';
        // Puede quedar [] (cliente excluido y sin envío configurado) → render
        // limpia la barra; NO es fallback manual.
        this.tiers = tiers;
      }

      _tierFromOffer(o, minEur) {
        var variants = [];
        (o.gift_products || []).forEach(function (gp) {
          (gp.variants || []).forEach(function (v) {
            var cents = Math.round((parseFloat(v.original_compare_at_price) || parseFloat(v.original_price) || 0) * 100);
            variants.push({
              keys: [v.id, v.original_variant_shopify_id, v.variant_shopify_id],
              price: cents,
              img: v.thumbnail || gp.thumbnail || '',
              // "🎁 Crema de almendra (100% off)" → "Crema de almendra"
              name: String(gp.title || '').replace(/^\s*🎁\s*/, '').replace(/\s*\(100% off\)\s*$/i, ''),
              product: gp.product_shopify_id || gp.id
            });
          });
        });
        if (!variants.length) return null;

        // Un regalo por PRODUCTO (foto + nombre para el tooltip).
        function pushGift(list, v) {
          for (var i = 0; i < list.length; i++) if (list[i].product === v.product) return;
          list.push({ product: v.product, url: v.img, name: v.name });
        }

        var gs = o.gift_settings || {};
        var auto = gs.customer_will_receive === 'auto_add_all_gifts';
        var value = 0, gifts = [];
        if (auto) {
          (gs.auto_add_all_gifts_quantity || []).forEach(function (row) {
            var v = variants.filter(function (x) { return x.keys.indexOf(row.id) !== -1; })[0];
            if (v) { value += v.price * (row.quantity || 1); pushGift(gifts, v); }
          });
          if (!value) {
            // Sin lista explícita: un regalo por producto.
            var seen = {};
            variants.forEach(function (v) {
              if (!seen[v.product]) { seen[v.product] = 1; value += v.price; pushGift(gifts, v); }
            });
          }
        } else {
          var n = parseInt(gs.number_of_gift_can_receive, 10) || 1;
          var sorted = variants.slice().sort(function (a, b) { return b.price - a.price; });
          value = (sorted[0] ? sorted[0].price : 0) * n;
          sorted.forEach(function (v) { pushGift(gifts, v); });
        }
        if (!value) return null;

        // Todas las claves de variante de los regalos de ESTA oferta (las tres
        // formas que publica BOGOS, igual que en giftMap). Sirven para saber si
        // un tramo de "elige regalo" sigue SIN reclamar: [[_choicePending]].
        var vkeys = [];
        variants.forEach(function (v) {
          v.keys.forEach(function (k) { if (k && vkeys.indexOf(k) === -1) vkeys.push(k); });
        });

        return {
          value: Math.round(minEur * 100),
          kind: 'gift',
          choice: !auto,
          vkeys: vkeys,
          gval: value,
          imgs: gifts.slice(0, 2).map(function (g) { return g.url; }).filter(Boolean),
          // Una foto por opción con NOMBRE distinto (cuadra con el texto del
          // tooltip): así "elige 1 de 3" muestra 3 fotos, no 4 con un duplicado.
          allImgs: (function () { var seen = {}, out = []; gifts.forEach(function (g) { if (g.url && g.name && !seen[g.name]) { seen[g.name] = 1; out.push(g.url); } }); return out; })(),
          names: gifts.map(function (g) { return g.name; }).filter(function (nm, i, a) { return nm && a.indexOf(nm) === i; }),
          hasTitle: !!(o.title || o.offer_name)
        };
      }

      /* ---- ¿queda algún regalo de "elige uno" sin reclamar? ---- */

      // Con TODOS los objetivos desbloqueados, lo único accionable que le puede
      // quedar al cliente es elegir el regalo de un tramo de tipo "elige uno"
      // (90€/110€). Un tramo conseguido cuenta como pendiente si NINGUNA de las
      // variantes de sus regalos está en el carrito.
      _choicePending(reached, cart) {
        if (!cart || !cart.items || !reached || !reached.length) return false;
        var inCart = {};
        cart.items.forEach(function (it) {
          var isClone = it.handle && it.handle.indexOf('sca_clone_freegift') !== -1;
          if (isClone || it.final_price === 0) inCart[it.variant_id] = 1;
        });
        return reached.some(function (t) {
          if (!t.choice || !t.vkeys || !t.vkeys.length) return false;
          return !t.vkeys.some(function (k) { return inCart[k]; });
        });
      }

      /* ---- valor de los regalos que ya están en el carrito ---- */

      _giftInCart(cart) {
        if (!cart || !cart.items) return null;
        var map = this._giftMap;
        if (!map) {
          // Sin fgData no podemos VALORAR los regalos, pero sí saber si quedan:
          // si ya no hay ninguna línea-clon, el valor correcto es 0 (evita que
          // el chip siga anunciando un regalo que el cliente acaba de quitar).
          var hasGift = cart.items.some(function (it) {
            return it.handle && it.handle.indexOf('sca_clone_freegift') !== -1;
          });
          return hasGift ? null : 0;
        }
        var sum = 0;
        cart.items.forEach(function (it) {
          var isClone = it.handle && it.handle.indexOf('sca_clone_freegift') !== -1;
          var v = map[it.variant_id];
          if (v && (isClone || it.final_price === 0)) sum += v * (it.quantity || 1);
        });
        return sum;
      }

      /* ---- render ---- */

      _rewardText(tier) {
        if (tier.kind === 'ship') return tier.title || '';
        var tpl = tier.choice ? this.rewardChoiceTpl : this.rewardGiftsTpl;
        return qmtxTpl(tpl, { value: qmtxMoney(tier.gval) });
      }

      _labelText(tier) {
        if (tier.kind === 'ship') return tier.title || '';
        if (tier.gval == null) return qmtxTpl(this.descTpl, { value: qmtxMoney(tier.value) });
        return qmtxTpl(tier.choice ? this.labelChoiceTpl : this.labelGiftsTpl, { value: qmtxMoney(tier.gval) });
      }

      // Tooltip (hover/click sobre el regalo): qué regalos son, con el precio
      // TACHADO → GRATIS (mismo lenguaje que las líneas REGALO del carrito).
      // Fotos del regalo EN GRANDE dentro del tooltip: al hacer click/tap el
      // cliente ve bien qué se lleva. Separadas (una izq / otra dcha) cuando el
      // tramo tiene dos regalos (elige 1 de estos). ~50px, ni enormes ni
      // diminutas. Solo si hay fotos y no es el envío.
      _tipImgs(tier) {
        if (tier.kind === 'ship') return '';
        var src = (tier.allImgs && tier.allImgs.length ? tier.allImgs : tier.imgs) || [];
        if (!src.length) return '';
        var imgs = src.slice(0, 4).map(function (u) {
          return '<img class="qmtx-progress__tip-img" src="' + String(u).replace(/"/g, '&quot;') + '" alt="" loading="lazy">';
        }).join('');
        return '<span class="qmtx-progress__tip-imgs' + (src.length === 2 ? ' is-pair' : '') + '">' + imgs + '</span>';
      }

      _tipHtml(tier) {
        if (tier.kind === 'ship') return tier.title || '';
        if (!tier.names || !tier.names.length) return '';
        var txt;
        if (tier.choice) {
          // En estrecho la etiqueta pierde el título ("Mega regalo") — el
          // tooltip del trofeo lo recupera aquí.
          txt = (tier.icon === 'trophy' ? this.topTitle + ' — ' : '') +
            'Elige 1: ' + tier.names.join(' o ') +
            ' · <s>' + qmtxMoney(tier.gval) + '</s> <b>GRATIS</b>';
        } else {
          txt = tier.names.join(' + ') + ' · <s>' + qmtxMoney(tier.gval) + '</s> <b>GRATIS</b>';
        }
        return this._tipImgs(tier) + '<span class="qmtx-progress__tip-txt">' + txt + '</span>';
      }

      // El data-tip queda como GANCHO (tap/zoom); el contenido va en un span
      // hijo real — attr() de CSS no permite tachados ni colores.
      _tipSpan(tier) {
        var h = this._tipHtml(tier);
        return h ? '<span class="qmtx-progress__tip" aria-hidden="true">' + h + '</span>' : '';
      }

      _imgsHtml(tier) {
        var multi = tier.imgs.length > 1 ? ' qmtx-progress__imgs--pair' : '';
        return '<span class="qmtx-progress__imgs' + multi + '">' + tier.imgs.map(function (u) {
          return '<img class="qmtx-progress__img" src="' + String(u).replace(/"/g, '&quot;') + '" alt="" loading="lazy">';
        }).join('') + '</span>';
      }

      _markerInner(tier, reached, isNext) {
        var check = reached ? '<span class="qmtx-progress__check">' + QMTX_CHECK_SVG + '</span>' : '';
        var icon = '<span class="qmtx-progress__icon qmtx-progress__icon--' + (tier.icon || 'gift') + '">' + qmtxIcon(tier.icon) + '</span>';
        // Jerarquía v3: el SIGUIENTE objetivo enseña los regalos FIJOS (hay que
        // verlos claros); TODOS los tramos de regalo lejanos alternan icono ↔
        // fotos como teaser (regalo/estrella/trofeo). En la pila, icono fijo.
        // EXCEPTO con el racimo expandido a petición (hover/tap): el cliente ha
        // pedido VER los regalos — fotos fijas, sin ciclo ni iconos.
        if (!reached && !isNext && tier.kind !== 'ship' && !this._clusterOpen && tier.imgs && tier.imgs.length) {
          return '<span class="qmtx-progress__swap">' +
            '<span class="qmtx-progress__swap-a">' + icon + '</span>' +
            '<span class="qmtx-progress__swap-b">' + this._imgsHtml(tier) + '</span>' +
          '</span>' + check;
        }
        // Resto (siguiente objetivo Y conseguidos, elegibles incluidos): FOTOS
        // del regalo siempre que existan; solo el envío gratis conserva icono.
        if (tier.kind !== 'ship' && tier.imgs && tier.imgs.length) {
          return this._imgsHtml(tier) + check;
        }
        return icon + check;
      }

      _tipAttr(tier) {
        return this._tipHtml(tier) ? ' data-tip="1"' : '';
      }

      // Reposiciona el tooltip px-aware al ABRIRLO (hover/tap) — en ese momento
      // el drawer ya está quieto (medir en cada _paint daría rects falsos a
      // mitad de la animación de apertura). Desplaza la caja con --tip-shift si
      // rebasaría el borde del drawer/página y ancla el pico con --tip-arrow
      // sobre el centro del marker. Sin medición (fallback) queda centrado.
      _placeTip(hit) {
        var tip = hit.querySelector('.qmtx-progress__tip');
        if (!tip) return;
        tip.style.setProperty('--tip-shift', '0px');
        var ref = this.closest('.m-cart-drawer') || this.closest('.qmtx-cart-page') || this;
        var M = 10;
        var r = tip.getBoundingClientRect();
        var rc = ref.getBoundingClientRect();
        var shift = 0;
        var over = r.right - (rc.right - M);
        if (over > 0) shift = -over;
        var under = (rc.left + M) - (r.left + shift);
        if (under > 0) shift += under;
        var mk = hit.getBoundingClientRect();
        var arrow = (mk.left + mk.width / 2) - (r.left + shift);
        arrow = Math.max(12, Math.min(r.width - 12, arrow));
        tip.style.setProperty('--tip-shift', Math.round(shift) + 'px');
        tip.style.setProperty('--tip-arrow', Math.round(arrow) + 'px');
      }

      render(total, cart) {
        // Total PREVIO antes de pisarlo: detecta crecer en € (añadir producto).
        // Comparar € y no % inmuniza contra recolocaciones (abrir/cerrar peek).
        var prevTotal = (typeof this._lastTotal === 'number') ? this._lastTotal : null;
        this._lastTotal = total;
        this._shipByCoupon = false;
        if (cart) { this._lastCart = cart; qmtxNoteCart(cart); }
        // Firma anti-bucle y anti-stale del _refresh: total + nº de líneas
        // (los regalos son 0€ → solo el item_count delata añadir/quitar uno).
        this._lastSig = total + ':' + (this._lastCart && typeof this._lastCart.item_count === 'number' ? this._lastCart.item_count : 'x');

        // Tiers efectivos: auto (BOGOS) o fallback a los goals de ajustes.
        // this.tiers === [] es un estado terminal válido (cliente excluido de
        // todas las ofertas y sin envío configurado): barra vacía, sin fallback.
        var tiers = this.tiers || this.goals.map(function (g) {
          return { value: g.value, kind: g.icon === 'truck' ? 'ship' : 'gift', title: g.title, icon: g.icon, gval: null, choice: false };
        });
        if (!tiers.length) { this._structSig = null; this._paint(''); return; }

        var n = tiers.length, self = this;
        var reached = tiers.filter(function (t) { return total >= t.value; });
        var upcoming = tiers.filter(function (t) { return total < t.value; });
        var base = reached.length ? reached[reached.length - 1].value : 0;

        // Pila de conseguidos como ELEMENTO flex (px reales) y barra flex:1 al
        // lado: el relleno nace EXACTAMENTE donde acaba la pila. Sin medir
        // anchos ni mezclar px y % — no puede quedar despegado. (v3: 26px
        // compactos — lo conseguido pesa menos que lo que viene.)
        var stackPx = reached.length ? (26 + (reached.length - 1) * 14 + 8) : 0;

        // Espacios estrechos (drawer siempre; carrito en móvil): los tramos más
        // allá de los 2 siguientes se COLAPSAN en un racimo compacto a la
        // derecha (espejo de la pila de conseguidos) con la suma de su valor.
        // Al acercarse el cliente, se expanden solos a hitos completos.
        // Contexto ESTRECHO (drawer siempre; carrito en móvil): además de
        // colapsar el racimo, las etiquetas del siguiente van sin título.
        var tightCtx = this._isDrawer || (this._mql && this._mql.matches);
        // >2 (no >3): con 3 pendientes en estrecho la pila roba ancho y los 3
        // "+X€ GRATIS" completos se pisan (9px medidos) → 2 hitos + racimo.
        var compact = tightCtx && upcoming.length > 2 && !this._clusterOpen;
        var fullUp = compact ? upcoming.slice(0, 2) : upcoming;
        var clustered = compact ? upcoming.slice(2) : [];
        var slots = fullUp.length + (clustered.length ? 1 : 0);

        // Hitos repartidos con aire — los textos del hover tienen sitio y no
        // se pisan. Con 0 cumplidos el reparto es SIMÉTRICO 14%→86% (con 3
        // objetivos el del medio queda EXACTO en el centro, sin recorrido
        // muerto ni extremos demasiado separados — afinado con Toti). Con >=1
        // cumplido entra el fake de media distancia y el siguiente necesita
        // recorrido: 22%→92% (+suelo). El fill interpola contra positions.
        var eL = this._clusterOpen ? 10 : (reached.length ? 22 : 14);
        var eR = this._clusterOpen || reached.length ? 92 : 86;
        var allPos = [];
        for (var si = 0; si < slots; si++) allPos.push(slots === 1 ? 50 : eL + si * (eR - eL) / (slots - 1));
        var positions = allPos.slice(0, fullUp.length);
        var clusterPos = clustered.length ? allPos[slots - 1] : 0;
        // Suelo del tramo activo ESCALADO: min(28, (100-50/n)/n) — el valor que
        // iguala el hueco de entrada al paso entre hitos (ritmo constante en
        // cualquier ancho). Con 2-3 slots equivale al 28% verificado (28/27.78);
        // con 4-5 evita el hueco muerto gigante en tracks anchos (900px y 5
        // slots: entrada 162px = paso; antes 252px vs 139px). No aplica en peek.
        // Suelo del tramo activo SOLO con >=1 cumplido (da recorrido al fake de
        // media distancia). Con 0 cumplidos manda la simetría de arriba.
        var floorP = Math.min(28, (100 - 50 / slots) / slots);
        if (reached.length && !this._clusterOpen && total > 0 && positions.length > 1 && positions[0] < floorP) {
          var pLast = clustered.length ? clusterPos : positions[positions.length - 1], p0 = positions[0];
          positions = positions.map(function (p) { return floorP + (p - p0) * (pLast - floorP) / (pLast - p0); });
        }
        this.classList.toggle('qmtx-progress--peek', !!this._clusterOpen);

        // Relleno: del inicio de la barra (= fin de la pila) hasta el siguiente
        // hito, proporcional en €.
        var fill;
        if (!upcoming.length) fill = 100;
        else if (total <= 0) fill = 0;
        else {
          var pv = base, pp = 0; fill = 100;
          for (var i = 0; i < fullUp.length; i++) {
            var gv = fullUp[i].value, gp = positions[i];
            if (total < gv) { fill = pp + (total - pv) / (gv - pv) * (gp - pp); break; }
            pv = gv; pp = gp;
          }
          // FAKE de media distancia: con >=1 objetivo cumplido, si el avance
          // real entre el completado y el siguiente va corto, la línea se
          // enseña POR LA MITAD del tramo. Si el avance real supera la mitad,
          // manda el real (y muere exacto en el hito). Nada más se toca.
          if (reached.length && upcoming.length) {
            fill = Math.max(fill, positions[0] * 0.5);
          }
        }

        // Total posible de regalos (suma de todos los tramos BOGOS, elegibles
        // al máximo) — lo usan el mensaje de carrito vacío y el chip.
        var maxPoss = 0;
        tiers.forEach(function (t) { if (t.kind !== 'ship' && t.gval) maxPoss += t.gval; });

        // CUPÓN QUE DEJA EL PEDIDO BAJO EL ENVÍO GRATIS (incidente 2026-10-16,
        // aprobado por Toti). Shopify compara la tarifa "≥ 29,90 €" con el
        // total YA descontado: con AFETEMP (250 € fijos) el pedido quedaba en
        // 0 €, la barra lo trataba como carrito vacío (gancho de regalos) y
        // solo se leía "Envío gratis" en azul bajo el camión... y en el pago
        // salían 2,90 € de envío. Sin "Añade X €": con un cupón fijo sin
        // agotar, añadir eso no basta (el cupón se lo come).
        var shipTier = null;
        tiers.forEach(function (t) { if (!shipTier && t.kind === 'ship') shipTier = t; });
        var codeCut = 0;
        if (shipTier && total < shipTier.value) {
          if (this._lastCart) {
            var appliedCodes = qmtxAppliedCodes(this._lastCart);
            // Un código aplicable que no resta nada a los productos es uno de
            // ENVÍO gratis (enviogratis2026 combina con los de pedido): el
            // envío ya sale gratis, el aviso mentiría.
            var shipCode = appliedCodes.some(function (c) { return !c.amount; });
            if (!shipCode) appliedCodes.forEach(function (c) { codeCut += c.amount; });
          } else {
            codeCut = this._seedCodeCut || 0;
          }
        }
        var shipByCoupon = codeCut > 0 && total + codeCut >= shipTier.value;
        // La tira de la ficha de producto lee este estado: con el cupón sin
        // agotar, "Añádelo y desbloqueas Envío gratis" tampoco es verdad.
        this._shipByCoupon = shipByCoupon;

        // Mensaje principal. (Sin chip de éxito: lo conseguido lo cuenta el chip
        // verde acumulado, un solo mensaje con fondo en vez de dos casi iguales.)
        // Carrito VACÍO: el gancho del total posible ES el mensaje — el texto
        // genérico no vendía y el chip lo duplicaba (una fila menos en el drawer).
        var msg;
        if (shipByCoupon) {
          msg = qmtxTpl(this.shipCouponTpl, { total: '<b>' + qmtxMoney(Math.max(0, total)) + '</b>', threshold: '<b>' + qmtxMoney(shipTier.value) + '</b>' });
        } else if (total <= 0) {
          msg = maxPoss > 0
            ? qmtxTpl(this.cumeTeaseTpl, { max: '<b class="qmtx-progress__reward">' + qmtxMoney(maxPoss) + '</b>' })
            : this.emptyMsg;
        } else if (!upcoming.length) {
          msg = this.unlockedMsg;
        } else {
          var next = upcoming[0];
          var remaining = '<b>' + qmtxMoney(next.value - total) + '</b>';
          if (next.gval != null || next.kind === 'ship') {
            msg = qmtxTpl(this.valueTpl, { remaining: remaining, reward: '<b class="qmtx-progress__reward">' + this._rewardText(next) + '</b>', goal: next.title || '' });
          } else {
            msg = qmtxTpl(this.countdownTpl, { remaining: remaining, goal: next.title || '' });
          }
        }

        // Pila de conseguidos (foto + check), el más reciente encima. Con
        // tooltip (hover/click) para ver qué regalo es cada mini-círculo.
        var stack = reached.map(function (t, i) {
          return '<span class="qmtx-progress__stackitem" style="left:' + (i * 14) + 'px;z-index:' + (i + 1) + ';"' + self._tipAttr(t) + '>' +
            self._markerInner(t, true, false) + self._tipSpan(t) + '</span>';
        }).join('');

        // Hitos visibles: el SIGUIENTE grande (38px) con regalos fijos; los
        // lejanos compactos y apagados, alternando icono↔fotos EN UNISONO.
        var markers = fullUp.map(function (t, i) {
          var isNext = i === 0;
          var peek = !isNext && self._clusterOpen ? ' is-peek' : '';
          return '<div class="qmtx-progress__goal' + (isNext ? ' is-next' : '') + peek + '" data-gi="' + i + '" style="left:' + positions[i] + '%"' + self._tipAttr(t) + '>' +
            self._markerInner(t, false, isNext) + self._tipSpan(t) + '</div>';
        }).join('');
        // Etiquetas con JERARQUÍA gasto/premio: el <b> (umbral) es una COORDENADA
        // de gasto — gris pequeña, con carrito 🛒 a la izquierda de CADA importe
        // ("esto es lo que gastas"); el <span> (premio) es el HÉROE — regalo 🎁 +
        // importe + "GRATIS" en versalita. El premio del SIGUIENTE está SIEMPRE
        // visible (azul); el de los LEJANOS se revela al hover/tap (CSS) — así
        // por defecto solo se ve el umbral y la fila queda limpia.
        var GIFT_G = '<span class="qmtx-progress__giftg" aria-hidden="true">' + QMTX_GIFT_SVG + '</span>';
        var CART_G = '<span class="qmtx-progress__cartg" aria-hidden="true">' + QMTX_CART_SVG + '</span>';
        var FREE = '<i class="qmtx-progress__free">' + self.freeWord + '</i>';
        var prizeSpan = function (gval, showFree, prefix) {
          return '<span class="qmtx-progress__prize">' + GIFT_G + (prefix || '') + '+' + qmtxMoney(gval) + (showFree ? FREE : '') + '</span>';
        };
        var labels = fullUp.map(function (t, i) {
          var isNext = i === 0;
          var b, s, bTitle = false;
          if (t.kind !== 'ship' && t.gval == null) {
            b = t.title || qmtxMoney(t.value); bTitle = !!t.title;
            s = '<span>' + qmtxTpl(self.descTpl, { value: qmtxMoney(t.value) }) + '</span>'; // gasto puro: sin glyph
          } else if (!isNext && t.kind !== 'ship') {
            // LEJANOS (y racimo abierto): en reposo SOLO el umbral — su premio
            // "+X€ GRATIS" está en el DOM pero OCULTO (CSS) y aparece al
            // hover/tap del hito, SOLO el tocado (uno a la vez → nunca chocan).
            // El único premio constante en la fila es el del SIGUIENTE.
            b = qmtxMoney(t.value);
            s = prizeSpan(t.gval, true);
          } else if (t.choice) {
            // A elegir SIGUIENTE: en estrecho sin título (mensaje+tooltip ya lo
            // cuentan); en desktop el título precede al premio.
            b = qmtxMoney(t.value);
            var pfx = tightCtx ? '' : ((t.icon === 'trophy' ? self.topTitle : self.choiceTitle) + ' · ');
            s = prizeSpan(t.gval, true, pfx);
          } else if (t.kind !== 'ship') {
            // Automático SIGUIENTE: "🎁 +13,40€ GRATIS" (azul vía .is-next).
            b = qmtxMoney(t.value);
            s = prizeSpan(t.gval, true);
          } else {
            b = qmtxMoney(t.value);
            s = '<span>' + self._labelText(t) + '</span>'; // envío: sin glyph
          }
          return '<div class="qmtx-progress__label' + (isNext ? ' is-next' : '') + '" data-gi="' + i + '" style="left:' + positions[i] + '%"><b>' + (!bTitle ? CART_G : '') + b + '</b>' + s + '</div>';
        }).join('');

        // Racimo de futuros (modo compacto): mini-fotos solapadas + suma GRATIS.
        if (clustered.length) {
          var cSum = 0;
          clustered.forEach(function (t) { cSum += t.gval || 0; });
          var minis = clustered.slice(0, 3).map(function (t, j) {
            var u = t.imgs && t.imgs[0];
            return '<span class="qmtx-progress__mini" style="left:' + (j * 9) + 'px;z-index:' + (3 - j) + '">' +
              (u ? '<img src="' + String(u).replace(/"/g, '&quot;') + '" alt="" loading="lazy">' : qmtxIcon(t.icon)) + '</span>';
          }).join('');
          var minisW = 18 + (Math.min(clustered.length, 3) - 1) * 9;
          markers += '<div class="qmtx-progress__goal qmtx-progress__goal--cluster" data-gi="c" style="left:' + clusterPos + '%">' +
            '<span class="qmtx-progress__minis" style="width:' + minisW + 'px">' + minis + '</span></div>';
          // Racimo real (2+): "desde X€" + la SUMA con GRATIS (el gancho de la
          // derecha). Racimo de 1 = un lejano más: solo el umbral con carrito,
          // su premio en el hover/click.
          labels += '<div class="qmtx-progress__label qmtx-progress__label--cluster" data-gi="c" style="left:' + clusterPos + '%">' +
            '<b>' + (clustered.length === 1 ? CART_G : 'desde ') + qmtxMoney(clustered[0].value) + '</b>' +
            (clustered.length > 1 ? prizeSpan(cSum, true) : '') + '</div>';
        }

        // Chip acumulado con el TOTAL POSIBLE ("Llevas 15,80€ de 48,60€
        // posibles en regalos GRATIS"): el hueco entre llevas↔posibles empuja.
        // Sin regalos aún, el gancho: "Puedes llevarte hasta 48,60€…" — visible
        // desde el carrito vacío. El máximo sale de los tramos de BOGOS
        // (elegibles al máximo); en fallback manual (sin valores) texto clásico.
        var giftInCart = this._giftInCart(this._lastCart);
        if (giftInCart != null) this._giftTotal = giftInCart;
        this._cumeText = '';
        if (this._giftTotal > 0) {
          this._cumeText = maxPoss > 0
            ? qmtxTpl(this.cumeTpl, { value: '<b>' + qmtxMoney(this._giftTotal) + '</b>', max: '<b>' + qmtxMoney(maxPoss) + '</b>' })
            : 'Ya llevas <b>' + qmtxMoney(this._giftTotal) + '</b> en regalos GRATIS';
        } else if (maxPoss > 0 && total > 0) {
          // Con carrito vacío el gancho ya es el MENSAJE: no duplicar en chip.
          this._cumeText = qmtxTpl(this.cumeTeaseTpl, { max: '<b>' + qmtxMoney(maxPoss) + '</b>' });
        }

        // TODOS LOS OBJETIVOS DESBLOQUEADOS (aprobado por Toti 2026-07-20).
        // Antes se veía "¡Has desbloqueado todos los objetivos!" y justo debajo
        // "Llevas 38,20€ de 51,80€ posibles" — se contradicen. El hueco no es
        // un fallo del cliente: el máximo cuenta SIEMPRE la opción más cara de
        // los tramos "elige regalo", así que nunca se alcanza eligiendo otra.
        // Se funden mensaje y chip en una sola línea (una fila menos en el
        // drawer) y el "de X posibles" desaparece.
        var allDone = total > 0 && reached.length > 0 && !upcoming.length;
        if (allDone) {
          if (this._choicePending(reached, this._lastCart) && this.cumeChoiceTpl) {
            // Lo único que le queda por hacer: que no se vaya sin su regalo.
            this._cumeText = this.cumeChoiceTpl;
            msg = '';
          } else if (this._giftTotal > 0 && this.cumeAllTpl) {
            this._cumeText = qmtxTpl(this.cumeAllTpl, { value: '<b>' + qmtxMoney(this._giftTotal) + '</b>' });
            msg = '';
          }
          // Si no hay chip que enseñar (sin valores de fgData) se conserva el
          // mensaje de desbloqueo: nunca una barra muda.
        }
        // "Completa tu regalo": solo con carrito activo y siguiente tramo cerca.
        var upsell = '';
        if (total > 0 && upcoming.length) upsell = this._upsellHtml(upcoming[0], upcoming[0].value - total);

        // FUSIÓN chip↔mensaje (P2): en el DRAWER, cuando hay chip verde y el
        // upsell está PLEGADO (o no existe), el chip del pie y el mensaje de
        // arriba son dos líneas casi redundantes que comen ~20px del listado.
        // Se funden: el chip sube como segunda línea compacta bajo el mensaje y
        // desaparece del pie (mismo elemento .qmtx-progress__cume → el pop del
        // confeti y el fast-path [data-cume-text] siguen anclados). Nunca en
        // /cart de escritorio (ahí sobra sitio) ni con el upsell ABIERTO (esa
        // fila ya ocupa su alto). Colapsado = la cabecera con '--collapsed';
        // ausente/descartado = ''. Con el upsell abierto la cabecera es '--open'.
        var upsellCollapsed = !upsell || upsell.indexOf('qmtx-progress__upsell--collapsed') !== -1;
        var fuseCume = this._isDrawer && !!this._cumeText && upsellCollapsed;

        var cume = '';
        if (this._cumeText) {
          cume = '<div class="qmtx-progress__cume-wrap' + (fuseCume ? ' qmtx-progress__cume-wrap--fused' : '') + '"><span class="qmtx-progress__cume">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v8H4v-8M2 7h20v5H2zM12 20V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>' +
            '<span data-cume-text>' + this._cumeText + '</span></span></div>';
        }

        // Píldora(s) de oferta POR LINK: el cliente que entró con código
        // (?freegifts_code=X) ve claro que sus regalos ADICIONALES están
        // armados (azul, "a partir de X€") o ya incluidos (verde ✓). El texto
        // sale del "Mensaje del carrito" de la oferta en BOGOS — cero
        // mantenimiento al crear nuevas ofertas-link. Sin código: nada.
        var linkHtml = '';
        if (this._linkAll && this._linkAll.length) {
          var codes = qmtxFgCodes();
          if (codes.length) {
            this._linkAll.forEach(function (lo) {
              if (codes.indexOf(lo.code) === -1 || !lo.msg) return;
              var done = total >= lo.min;
              var txt = qmtxTpl(done ? self.codeDoneTpl : self.codeArmedTpl,
                { msg: '<b>' + lo.msg + '</b>', min: qmtxMoney(lo.min) });
              linkHtml += '<div class="qmtx-progress__code-wrap"><span class="qmtx-progress__code qmtx-progress__code--' + (done ? 'done' : 'armed') + '">' +
                (done ? QMTX_CHECK_SVG : QMTX_GIFT_SVG) + '<span>' + txt + '</span></span></div>';
            });
            if (linkHtml) {
              try {
                if (!sessionStorage.getItem('qmtx-code-seen')) {
                  sessionStorage.setItem('qmtx-code-seen', '1');
                  qmtxTrack('pb_code_active', { codes: codes.join(','), cart_total_eur: total / 100 });
                }
              } catch (e) {}
            }
          }
        }

        // Camino rápido: si la ESTRUCTURA no cambió (mismos tramos conseguidos/
        // pendientes, chip y upsell iguales), mutamos in place — así la
        // transición CSS del relleno anima de verdad y el pulso del siguiente
        // hito no se reinicia en cada refresco.
        var structSig = (this.tiers ? 'a' : 'm') + '|' + reached.length + '|' +
          upcoming.map(function (t) { return t.value; }).join(',') + '|k' + (compact ? 1 : 0) + '|t' + (tightCtx ? 1 : 0) + '|' + (cume ? 1 : 0) + '|u' + upsell.length + '|f' + (fuseCume ? 1 : 0) + '|c' + linkHtml.length + '|m' + (msg ? 1 : 0);
        if (structSig === this._structSig) {
          var fillEl = this.querySelector('.qmtx-progress__fill');
          var msgEl = this.querySelector('.qmtx-progress__msg');
          var cumeEl = this.querySelector('[data-cume-text]');
          if (fillEl && msgEl) {
            this._selfPaint = true;
            fillEl.style.width = fill + '%';
            msgEl.innerHTML = msg;
            if (cumeEl) cumeEl.innerHTML = this._cumeText;
            var self2 = this;
            setTimeout(function () { self2._selfPaint = false; }, 0);
            // Estructura igual ⇒ aquí nunca hay desbloqueo/confeti: el cometa
            // procede siempre que el total crezca (add/qty sin cruzar hito).
            this._advancePulse(prevTotal, total);
            // El tema pudo re-renderizar el sumario (secciones): recolocar el
            // desglose de regalos adicionales si toca.
            this._syncCodeSavings();
            return;
          }
        }
        this._structSig = structSig;

        // Si este repintado desbloquea tramo hablará el confeti (_afterPaint
        // compara _prevReached): el cometa calla — una sola voz por cambio.
        var unlockNow = this._prevReached != null && reached.length > this._prevReached;
        this._paint(
          (msg ? '<div class="qmtx-progress__msg">' + msg + '</div>' : '') +
          (fuseCume ? cume + linkHtml : '') +
          '<div class="qmtx-progress__row">' +
            (stack ? '<div class="qmtx-progress__stack" style="width:' + stackPx + 'px">' + stack + '</div>' : '') +
            '<div class="qmtx-progress__track">' +
              '<div class="qmtx-progress__fill" style="width:' + fill + '%"></div>' +
              markers +
            '</div>' +
          '</div>' +
          (labels ? '<div class="qmtx-progress__labels' + (slots > 3 ? ' qmtx-progress__labels--dense' : '') + '" style="margin-left:' + (22 + stackPx) + 'px">' + labels + '</div>' : '') +
          (fuseCume ? '' : cume + linkHtml) +
          upsell
        );
        if (!unlockNow) this._advancePulse(prevTotal, total);
        this._syncCodeSavings();
      }

      // Escritura de DOM con supresión del eco en el MutationObserver.
      _paint(html) {
        this._selfPaint = true;
        this.innerHTML = html;
        var self = this;
        setTimeout(function () { self._selfPaint = false; }, 0);
        this._afterPaint();
      }

      // Tras pintar: confetti al desbloquear un tramo y empujoncito del slider.
      _afterPaint() {
        var self = this;
        var reachedNow = (this.querySelectorAll('.qmtx-progress__stackitem') || []).length;
        if (this._prevReached != null && reachedNow > this._prevReached) {
          this._celebrate();
          qmtxTrack('pb_tier_unlocked', { tiers_reached: reachedNow, cart_total_eur: (this._lastTotal || 0) / 100, currency: 'EUR' });
          // Al pedido: cuántos tramos de regalo llevaba el carrito (Admin/export)
          qmtxCartAttr({ 'Tramos de regalo': reachedNow });
        }
        this._prevReached = reachedNow;

        // "Esto se desliza": la fila se mueve 30px y vuelve, una vez por sesión.
        var row = this.querySelector('.qmtx-progress__upsell-row');
        if (row && !this._nudged && row.scrollWidth > row.clientWidth) {
          this._nudged = true;
          setTimeout(function () {
            try {
              row.scrollTo({ left: 34, behavior: 'smooth' });
              setTimeout(function () { row.scrollTo({ left: 0, behavior: 'smooth' }); }, 480);
            } catch (e) {}
          }, 700);
        }
      }

      // Mini-confetti sobre la barra + "pop" del chip verde (sin librerías).
      _celebrate() {
        try { if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return; } catch (e) {}
        var row = this.querySelector('.qmtx-progress__row');
        if (!row) return;
        var self = this;
        var stack = this.querySelector('.qmtx-progress__stack');
        var holder = document.createElement('span');
        holder.className = 'qmtx-confetti';
        holder.style.left = (stack ? stack.offsetWidth : 10) + 'px';
        var colors = ['#5d89f9', '#38b26a', '#f6b73c', '#e2574c'];
        for (var i = 0; i < 16; i++) {
          var p = document.createElement('i');
          var ang = (30 + Math.random() * 120) * Math.PI / 180;
          var dist = 28 + Math.random() * 44;
          p.style.setProperty('--dx', Math.round(Math.cos(ang) * dist * (Math.random() < 0.5 ? -1 : 1)) + 'px');
          p.style.setProperty('--dy', Math.round(-Math.sin(ang) * dist) + 'px');
          p.style.background = colors[i % colors.length];
          p.style.animationDelay = Math.round(Math.random() * 140) + 'ms';
          holder.appendChild(p);
        }
        this._selfPaint = true;
        row.appendChild(holder);
        var chip = this.querySelector('.qmtx-progress__cume');
        if (chip) chip.classList.add('qmtx-pop');
        setTimeout(function () { self._selfPaint = false; }, 0);
        setTimeout(function () {
          self._selfPaint = true;
          holder.remove();
          if (chip) chip.classList.remove('qmtx-pop');
          setTimeout(function () { self._selfPaint = false; }, 0);
        }, 1400);
      }

      // Desglose "Regalos {msg} adicionales −X€" bajo la línea "Ahorro en
      // regalos" del sumario, cuando hay código de oferta-por-link ACTIVO y
      // su umbral cumplido. El tema re-renderiza el bloque por secciones →
      // insert-if-missing tras cada repintado (autocura); _selfPaint evita
      // el eco con nuestro MutationObserver.
      _syncCodeSavings() {
        var root = this.closest('.m-cart-drawer') || document;
        var anchor = root.querySelector('[data-gift-savings]');
        var self = this;
        this._selfPaint = true;
        try {
          root.querySelectorAll('[data-code-savings]').forEach(function (n) { n.remove(); });
          if (anchor && this._linkAll && this._linkAll.length) {
            var codes = qmtxFgCodes();
            var total = this._lastTotal || 0;
            var html = '';
            if (codes.length) {
              this._linkAll.forEach(function (lo) {
                if (codes.indexOf(lo.code) === -1 || !lo.gval || !lo.msg || total < lo.min) return;
                html += '<div class="qmtx-savings-line qmtx-savings-line--code" data-code-savings>' +
                  '<span class="qmtx-savings-line__label">' + QMTX_GIFT_SVG +
                  '<span>' + qmtxTpl(self.codeSavingsTpl, { msg: lo.msg }) + '</span></span>' +
                  '<span class="qmtx-savings-line__amount">-' + qmtxMoney(lo.gval) + '</span></div>';
              });
            }
            if (html) anchor.insertAdjacentHTML('afterend', html);
          }
        } catch (e) {}
        setTimeout(function () { self._selfPaint = false; }, 0);
      }

      // Refuerzo del avance: cuando el TOTAL crece (añaden producto), un
      // cometa recorre el relleno UNA vez (0.6s). Nunca en reposo, nunca en
      // loop, nunca si el cambio ya celebra con confeti. Solo toca la CLASE
      // del fill (el MutationObserver escucha childList: sin eco).
      _advancePulse(prevTotal, total) {
        if (prevTotal == null || total <= prevTotal) return;
        try { if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return; } catch (e) {}
        var fillEl = this.querySelector('.qmtx-progress__fill');
        if (!fillEl) return;
        var self = this;
        fillEl.classList.remove('qmtx-progress__fill--surge');
        void fillEl.offsetWidth; // reinicia el one-shot si añaden 2 seguidos
        fillEl.classList.add('qmtx-progress__fill--surge');
        clearTimeout(this._surgeT);
        this._surgeT = setTimeout(function () {
          var f = self.querySelector('.qmtx-progress__fill');
          if (f) f.classList.remove('qmtx-progress__fill--surge');
        }, 700);
      }
    }
    customElements.define('qmtx-progress', QmtxProgress);
  }

  /**
   * <qmtx-pdp-strip> — franja bajo el botón "Añadir al carrito" de la ficha:
   * "🎁 Estás a 6,10€ de +13,40€ en regalos GRATIS" (o "Añádelo y desbloqueas…"
   * si el precio de este producto cubre lo que falta). No fetchea nada: lee
   * los tramos y el total del <qmtx-progress> del drawer, que ya se mantiene
   * al día solo. Se refresca con los mismos eventos de carrito.
   */
  if (!customElements.get('qmtx-pdp-strip')) {
    var QMTX_STRIP_GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v8H4v-8M2 7h20v5H2zM12 20V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';

    class QmtxPdpStrip extends HTMLElement {
      connectedCallback() {
        this._price = parseInt(this.getAttribute('data-price') || '0', 10) || 0;
        this._tpl = this.getAttribute('data-tpl') || '🎁 Estás a {remaining} de {reward}';
        this._unlockTpl = this.getAttribute('data-unlock-tpl') || '🎁 Añádelo y desbloqueas {reward}';

        var self = this, tries = 0;
        this._sync = function () { self._render(); };

        // La barra del drawer construye sus tramos async (fgData): espera a
        // que estén y píntate; después, refresco con los eventos de carrito.
        this._waiter = setInterval(function () {
          tries++;
          if (self._render() || tries >= 24) { clearInterval(self._waiter); self._waiter = null; }
        }, 500);
        this._render();

        this._events = ['cart:updated', 'cart:refresh', 'cart:changed', 'minimog:cart:update', 'minimog:cart:refresh'];
        this._events.forEach(function (n) { document.addEventListener(n, self._sync); });
        this._mo = null;
        var root = document.getElementById('MinimogCartDrawer');
        if (root && window.MutationObserver) {
          this._mo = new MutationObserver(function () {
            clearTimeout(self._t);
            self._t = setTimeout(self._sync, 250); // deja que la barra resincronice antes
          });
          this._mo.observe(root, { childList: true, subtree: true });
        }

        // Cambio de variante en la ficha (300g → 600g): el tema emite
        // 'variant:changed' con la variante — recalculamos con SU precio si
        // este producto pasa a cubrir (o dejar de cubrir) lo que falta.
        this._onVariant = function (e) {
          var v = e && e.detail && e.detail.variant;
          if (v && typeof v.price === 'number') { self._price = v.price; self._render(); }
        };
        document.addEventListener('variant:changed', this._onVariant);
      }

      disconnectedCallback() {
        var self = this;
        if (this._events && this._sync) this._events.forEach(function (n) { document.removeEventListener(n, self._sync); });
        if (this._mo) this._mo.disconnect();
        if (this._waiter) clearInterval(this._waiter);
        if (this._onVariant) document.removeEventListener('variant:changed', this._onVariant);
      }

      // true = pintado con datos válidos.
      _render() {
        var src = document.querySelector('qmtx-progress');
        var tiers = src && src.tiers;
        if (!tiers || !tiers.length || typeof src._lastTotal !== 'number') { this.hidden = true; return false; }
        // Cupón que deja el pedido bajo el envío gratis: añadir este producto
        // no garantiza ningún tramo (el cupón se lo come). Mejor callar.
        if (src._shipByCoupon) { this.hidden = true; return true; }
        var total = src._lastTotal;
        var next = null;
        for (var i = 0; i < tiers.length; i++) { if (total < tiers[i].value) { next = tiers[i]; break; } }
        if (!next) { this.hidden = true; return true; } // todo desbloqueado
        var remaining = next.value - total;
        var reward = '<b class="qmtx-gain">' + src._rewardText(next) + '</b>'; // ganancia en verde
        var unlocks = this._price >= remaining && remaining > 0;
        var msg = unlocks
          ? qmtxTpl(this._unlockTpl, { remaining: '<b>' + qmtxMoney(remaining) + '</b>', reward: reward })
          : qmtxTpl(this._tpl, { remaining: '<b>' + qmtxMoney(remaining) + '</b>', reward: reward });
        this.innerHTML = QMTX_STRIP_GIFT + '<span>' + msg + '</span>';
        this.hidden = false;
        if (!this._viewed) {
          this._viewed = true;
          qmtxTrack('pb_strip_view', { state: unlocks ? 'unlock' : 'progress', remaining_eur: remaining / 100, currency: 'EUR' });
        }
        return true;
      }
    }
    customElements.define('qmtx-pdp-strip', QmtxPdpStrip);
  }
})();
